import React, { useState, useMemo, useEffect } from 'react';
import { calculateAge } from '../../utils/index.ts';
import { guaranteeWindowWords } from '../../components/strategy/annuityWords.ts';
import { guaranteedShapeAvailable } from '../../../../shared/src/calculation/carePlusSplit.ts';
import { annuityPreferenceOf, type AnnuityAppetite, type AnnuityResidual } from '../../../../shared/src/calculation/annuityPreference.ts';
import AnnuityPreferenceGate from '../../components/strategy/AnnuityPreferenceGate.tsx';
import { cashflowCoveredNote, CASHFLOW_COVERED_LABEL, GOAL_CHIP_LABELS, coveredIsBestEstate } from '../../strategy/cashflowCoveredGoal.ts';
import { scrollAppToTop } from '../../utils/scrollAppToTop.ts';
import { ChevronLeft, ArrowRight, Home, Wallet, Users, ShieldCheck, Scale, CalendarDays, TrendingUp, AlertTriangle, PiggyBank, BookOpen, Printer } from 'lucide-react';
import { openGfSolicitorBrief, openQuestionsFor, GF_ARRANGEMENT_FORM_LABELS, GF_BILLS_LABELS, GF_CARE_LABELS, GF_FAIRNESS_LABELS, GF_WHAT_IF_LABELS, type GfSolicitorBriefFigures } from './gfSolicitorBrief.ts';
import type { GrannyFlatFamilyGuideAnswers } from '../../../../shared/src/calculation/factFindTypes.ts';
import StrategyRisksPanel from '../../components/strategy/StrategyRisksPanel.tsx';
import { buildStrategyRisks, riskKindsFromCandidateId } from '../../strategy/strategyRisks.ts';
import { alternativeAssetNote } from '../../strategy/alternativeAssetNote.ts';
import { buildGrannyFlatSoaSnapshot, gfFamilyToKeepWhole } from './gfSoa.ts';
import { openModuleSoa } from '../advice/moduleSoa.ts';

import { Card, GradientText, CurrencyInput, InfoIcon, FeeExplanationAccordion, Button, IconTile } from '../../components/ui/index.tsx';
import * as calculationApi from '../../services/calculationApi.ts';
import { getGrannyFlatDeprivationFromFactFind } from '../../../../shared/src/calculation/grannyFlatArrangement.ts';
import { describeResidualDeployment } from '../../../../shared/src/calculation/residualDeployment.ts';
import { buildGrannyFlatSuggestions, getGrannyFlatReasonableValueForClient, gfEngineSetupFromGoals } from '../../strategy/grannyFlatRecommendedStrategies.ts';
import type { GrannyFlatConfig, GrannyFlatSuggestionsResult, GfEngineSetup } from '../../strategy/grannyFlatRecommendedStrategies.ts';
import { enrichStrategiesWithRecommendationLabels, buildRecommendedOptimalStrategies, mergeGoalLeaderStrategies, type EvaluatedStrategy, type RecommendedStrategyEntry } from '../../strategy/supportAtHomeRecommendedStrategies.ts';
import { buildGoalCards, visibleGoalCards, emptyGoalLine, type GoalFocus } from '../../strategy/engineGoalCards.ts';
import GoalChipRow from '../../components/strategy/GoalChipRow.tsx';
import AllStrategiesModal from '../../components/strategy/AllStrategiesModal.tsx';
import EngineStrategyCard from '../../components/strategy/EngineStrategyCard.tsx';
import SameAsBestEstateNote from '../../components/strategy/SameAsBestEstateNote.tsx';
import AnalyserSteps, { type AnalyserStep } from '../../components/strategy/AnalyserSteps.tsx';
import GfStayPutHero from './GfStayPutHero.tsx';
import GfAnalysisSetupModal from './GfAnalysisSetupModal.tsx';
import { GfInBrief, nilResidualAnnuityText, type GfInBriefAnnuity } from './gfInBrief.tsx';
import { annuityHorizonFlag, type HorizonFactFind } from '../agedCare/strategyHorizonFlags.ts';
import TransferCapGate from '../../components/TransferCapGate.tsx';
import { tbcQuestionNeededFor } from '../../../../shared/src/calculation/transferBalanceCap.ts';
import { nonConcessionalCapacityForHousehold } from '../../../../shared/src/calculation/nonConcessionalContributions.ts';
import { buildNetWorthSchedule } from '../../../../shared/src/calculation/netWorthSchedule.ts';
import FinancialImpactPanel from '../agedCare/FinancialImpactPanel.tsx';
import type { FinancialPosition } from '../agedCare/FinancialImpactPanel.tsx';
import { formatCurrency } from '../../utils/index.ts';
import type { MoneyFlowStep } from '../../components/narrative/PlainWords.tsx';
import { EXTRA_ALLOWABLE_AMOUNT, PENSION_RATES } from '../../constants/index.ts';
import GrannyFlatPlanRecap from './GrannyFlatPlanRecap.tsx';
import GrannyFlatArrangementNote from './GrannyFlatArrangementNote.tsx';
import { ModeChooser } from '../../components/ui/index.tsx';
import type { Financials, ProjectionYear, GrannyFlatInsightFinancials, MoneyFlowRow, SaleFlow } from './grannyFlatTypes.ts';
import SuggestionEngineAnalysisLoader from '../../components/ui/SuggestionEngineAnalysisLoader.tsx';
import NewMoneyProfileGate from '../../components/NewMoneyProfileGate.tsx';
import { hasAnsweredNewMoneyProfile } from '../../../../shared/src/calculation/investmentProfiles.ts';
import type { InvestmentProfileId } from '../../../../shared/src/calculation/investmentProfiles.ts';
import ProceedsDeploymentFields, { deploymentAnnuityTerms } from '../../components/strategy/ProceedsDeploymentFields.tsx';
import { EngineAnnuityTerms, AnnuityQuoteControl } from '../../components/strategy/AnnuityTerms.tsx';
import type { ProceedsDeploymentValue } from '../../components/strategy/ProceedsDeploymentFields.tsx';
import AssetFundingAllocator from '../../components/strategy/AssetFundingAllocator.tsx';
import ProjectionsModal from '../agedCare/ProjectionsModal.tsx';
import { readPeriodMode, type ProjectionsModalPeriodMode } from '../agedCare/projectionsModalPreferences.ts';
import PlanSummaryPanel from '../agedCare/PlanSummaryPanel.tsx';
import PlanExplanationModal from '../agedCare/PlanExplanationModal.tsx';
import { planRunwayCopy } from '../../components/insights/fundingSummary.tsx';
import { FundingRunway } from '../../components/narrative/index.tsx';
import DebtPayoffStrategy from '../agedCare/DebtPayoffStrategy.tsx';
import { applyDebtPayoffToFactFind, type DebtPayoff } from '../../../../shared/src/calculation/support/debtPayoff.ts';
import type { DraftFactFind } from '../../constants/initialFactFind.ts';
import type { FactFind } from '../../../../shared/src/calculation/factFindTypes.ts';
import type { ResidualDeployment } from '../../../../shared/src/calculation/residualDeployment.ts';

/** The non-null result of getGrannyFlatDeprivationFromFactFind (reasonable-value / deprivation breakdown). */
type DepResult = NonNullable<ReturnType<typeof getGrannyFlatDeprivationFromFactFind>>;

interface ExplainSection {
  id: string;
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  summary: string;
  details: React.ReactNode;
  applies: boolean;
}

/**
 * The investment property line is shown only to a client who has one on file (owner, 2 Oct 2026:
 * no need to confuse the others). The face-value forms name the title of the HOME; an investment
 * property signed over is assets given, and tested.
 */
const gfExplainSections = (hasInvestmentProperty: boolean): ExplainSection[] => [
  { id: 'what', icon: Home, title: 'What a granny flat arrangement is', summary: 'You give money or assets to family in exchange for a lifetime right to live in their home. It is a Centrelink concept — not necessarily a separate flat.', details: <p className="text-gray-700 leading-relaxed">A &quot;granny flat interest&quot; (or right) is a formal arrangement where you transfer money and/or assets — sometimes your own home — to a family member, in return for the right to live in their home for life. You don&apos;t have to build an actual granny flat. It can be very tax- and pension-effective, but it needs to be set up carefully.</p>, applies: true },
  { id: 'homeowner', icon: Wallet, title: 'Test 1 — Will Centrelink treat you as a homeowner?', summary: `Two separate tests apply, and this is the first. It asks one thing: how much you give in total. Above the extra allowable amount ($${EXTRA_ALLOWABLE_AMOUNT.toLocaleString()}), you're a homeowner and the right to live there is exempt from the assets test, like a home. At or below it, you're a non-homeowner.`, details: <div className="text-gray-700 leading-relaxed space-y-2"><p>This test only decides <strong>which Age Pension limits apply to you</strong>. Add up everything you give — cash, plus any home you transfer. If the total is <strong>above</strong> the extra allowable amount (currently ${EXTRA_ALLOWABLE_AMOUNT.toLocaleString()}), Centrelink treats you as a <strong>homeowner</strong>: your right to live there is exempt from the assets test, just as a home would be, and the lower homeowner asset limits apply. If the total is <strong>at or below</strong> it, you're a <strong>non-homeowner</strong>: the higher non-homeowner limits apply, and you may qualify for Rent Assistance.</p><p>Because you're turning counted savings into an exempt right, your Age Pension often <strong>goes up</strong>. But "exempt" is about the right you receive. It says nothing about whether you paid too much for it — that's the second test, below.</p></div>, applies: true },
  { id: 'reasonable', icon: Scale, title: 'Test 2 — Did you pay more than the right is worth? (the gift test)', summary: 'The second test asks a different question: did you give more than Centrelink says the right is worth? It depends on what you give. Sign over your home, or pay to build the accommodation, and Centrelink takes that at face value — no gift, whatever it costs. Pay cash and there is a limit: up to Centrelink\'s reasonable value nothing is a gift; above it, the excess is counted against you for five years.', details: <div className="text-gray-700 leading-relaxed space-y-2"><p><strong>Three ways of paying are taken at face value.</strong> If all you do is sign over the title of your home, pay for the building or extension of your accommodation, or buy a home in the family member&apos;s name, Centrelink accepts what that is worth as the price of the right. There is no gift, however large the amount — a $600,000 build is a $600,000 right. Keep the building contract and invoices: anything paid beyond the actual cost of the build counts as extra.</p><p><strong>Cash is tested.</strong> Pay cash, or add cash or other assets on top of one of the three above, and Centrelink works out a <strong>reasonable value</strong> for your lifetime right: the combined partnered pension rate multiplied by a factor for your age. The right is valued at the greater of that formula and what you provided (the home, the build). Whatever you give <strong>up to that value</strong> is the price of the right — no gift. The excess is treated as a gift: after the usual free area ($10,000 a year, $30,000 over five years) it is counted in your assets and deemed in your income for <strong>five years</strong>, then drops off.</p>{hasInvestmentProperty && <p><strong>Your investment property is not your home.</strong> The face-value rule is for the title of the home you live in. Sign an investment property over to family for the right and Centrelink treats it as assets given, like cash: the right is valued at the formula, and the property&apos;s value above that is a gift. Confirm this with Services Australia&apos;s Financial Information Service before relying on it.</p>}<p><strong>How the two tests fit together.</strong> Test 1 makes the right itself exempt. Test 2 catches any overpayment and counts it anyway. So you can be a homeowner with an exempt right <em>and</em> still have a gift counted against you, if you paid too much in cash. The calculator&apos;s &quot;pay the reasonable value&quot; option sits exactly at Centrelink&apos;s figure, so nothing is a gift.</p></div>, applies: true },
  { id: 'documentation', icon: ShieldCheck, title: 'Protect yourself — get it in writing', summary: 'An undocumented arrangement is risky: if things go wrong with the family member, you can lose both the money and the home.', details: <p className="text-gray-700 leading-relaxed">Always use a <strong>written agreement</strong>. If the family member divorces, dies, goes bankrupt, sells, or you fall out, an undocumented arrangement can leave you with no home and no money back. A written agreement makes your right enforceable and is what Centrelink expects. Get independent legal advice before transferring anything.</p>, applies: true },
  { id: 'estate', icon: Users, title: 'Fairness to other family', summary: 'Giving a large amount to one family member can leave others with much less — a common cause of will disputes. Plan for fairness.', details: <p className="text-gray-700 leading-relaxed">A granny-flat arrangement gives one family member a large slice of your wealth. If the rest of your estate is then split equally, the others can be left behind — a classic trigger for <strong>will disputes</strong>. Consider how to keep things fair (for example, adjusting your will) and talk to an estate-planning solicitor.</p>, applies: true },
];

interface GrannyFlatExplainedProps {
  setActivePage: (page: string) => void;
  onContinue: () => void;
  factFind: DraftFactFind;
  setFactFind?: React.Dispatch<React.SetStateAction<DraftFactFind>>;
}

const GrannyFlatExplained = ({ setActivePage, onContinue, factFind, setFactFind }: GrannyFlatExplainedProps) => {
  const [openAccordion, setOpenAccordion] = useState<string | null>(null);
  const toggle = (id: string) => setOpenAccordion(openAccordion === id ? null : id);
  const hasInvestmentProperty = (factFind?.assets?.investmentProperty || []).some((p) => (parseFloat(String(p?.value ?? '')) || 0) > 0);
  const sections = gfExplainSections(hasInvestmentProperty);
  // The family discussion guide, before any numbers (owner, 2 Oct 2026): families should be on the
  // same page before anyone models a payment. It was only reachable under the results.
  const [showGuide, setShowGuide] = useState(false);
  if (showGuide) {
    return (
      <GrannyFlatFamilyGuide
        factFind={factFind}
        setFactFind={setFactFind}
        onBack={() => setShowGuide(false)}
        backLabel="Back to the overview"
        onContinue={onContinue}
        continueLabel="Continue to the calculator"
        onBackToDashboard={() => setActivePage('Dashboard')}
      />
    );
  }
  return (
    <div className="max-w-6xl mx-auto">
      <Button variant="ghost" size="sm" icon={ChevronLeft} onClick={() => setActivePage('Dashboard')} className="mb-4 -ml-2">Back to Dashboard</Button>
      <div className="mb-8">
        <div className="flex items-center gap-4 mb-4">
          <IconTile icon={Home} tone="brand" variant="solid" size="lg" className="hidden sm:inline-flex" />
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900"><GradientText>Granny Flat Arrangement</GradientText></h1>
        </div>
        <p className="text-lg text-slate-700 leading-relaxed mb-3">Moving in with family and giving them money or your home for the right to live there? A granny-flat arrangement can be very pension- and tax-effective — but it must be set up carefully. Let&apos;s model what it means for you.</p>
        <p className="text-sm text-slate-500">This is general information and modelling only. A granny-flat arrangement is a significant legal and family decision — get independent legal, financial and estate-planning advice before you commit.</p>
      </div>
      <div className="space-y-4 mb-8">
        {sections.map((fee) => (
          <FeeExplanationAccordion key={fee.id} {...fee} isOpen={openAccordion === fee.id} onToggle={() => toggle(fee.id)} factFind={{}} />
        ))}
      </div>
      <Card className="mb-8">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:justify-between">
          <div className="flex items-start gap-3">
            <IconTile icon={Users} tone="brand" variant="soft" size="sm" className="hidden sm:inline-flex" />
            <div>
              <h3 className="text-xl font-bold text-gray-800">Talk it through as a family first</h3>
              <p className="text-sm text-gray-600 max-w-2xl">Questions to settle together before any money changes hands — what the right covers, who pays what, fairness to other family, and the &quot;what-ifs&quot; — with a brief you can print and hand to a solicitor.</p>
            </div>
          </div>
          <Button variant="secondary" size="sm" iconRight={ArrowRight} className="shrink-0" onClick={() => setShowGuide(true)}>Open the guide</Button>
        </div>
      </Card>
      <div className="text-center">
        <Button onClick={onContinue} variant="brand" size="lg" iconRight={ArrowRight}>Continue to calculator</Button>
      </div>
    </div>
  );
};

interface GfInputs {
  amountPaid: number;
  transferHome: boolean;
  propertyTransferredValue: number;
  constructionCost: number;
  dateEntered: string;
  fundFrom: 'savings' | 'sell_home';
  funding: Record<string, number>;
  fundingCgtOverrides: Record<string, number | null>;
  deployment: ProceedsDeploymentValue;
  justMovingIn: boolean;
  keptHomeUse: 'vacant' | 'rented';
  keptHomeRent: number;
  keptHomeExpenses: number;
}

interface GfBeforeAfter {
  before: Financials;
  after: Financials;
  simulated: DraftFactFind;
}

interface GrannyFlatFundingMeta {
  breakdown?: MoneyFlowRow[];
  cgt?: number;
  shortfall?: number;
}

/** A leader card from the Suggestion Engine — EvaluatedStrategy plus the fields this module reads off it. */
export interface GfStrategyView extends EvaluatedStrategy {
  id: string;
  description?: string;
  inputs?: Partial<GrannyFlatConfig>;
  projections?: ProjectionYear[];
  fundingBreakdown?: MoneyFlowRow[];
  fundingCgt?: number;
  fundingShortfall?: number;
  deprivation?: number;
}

interface SeView {
  name?: string;
  before: Financials;
  after: Financials;
  projections: ProjectionYear[];
  depResult: DepResult | null;
  hasArrangement: boolean;
  justMovingIn: boolean;
  amountPaid: number;
  propertyTransferred: number;
  entryContribution: number;
  isHomeowner: boolean;
  keepingHome: boolean;
  keptHomeRented: boolean;
  formerHomeValue: number;
  reasonableValue: number;
  interestValue: number;
  deprivation: number;
  legacyEstimate: number;
  fundingRows: MoneyFlowRow[];
  fundingCgt: number;
  fundingShortfall: number;
  deploymentRows: MoneyFlowRow[];
  saleFlow: SaleFlow | null;
  insightFinancials: GrannyFlatInsightFinancials;
}

interface PanelModel {
  name?: string | null;
  before: Financials;
  after: Financials;
  simulated?: DraftFactFind;
  depResult?: DepResult | null;
  hasArrangement: boolean;
  justMovingIn: boolean;
  amountPaid: number;
  propertyTransferred: number;
  entryContribution: number;
  isHomeowner: boolean;
  keepingHome: boolean;
  keptHomeRented: boolean;
  formerHomeValue: number;
  reasonableValue: number;
  interestValue: number;
  deprivation: number;
  legacyEstimate?: number;
  projections: ProjectionYear[];
  fundingRows: MoneyFlowRow[];
  fundingCgt: number;
  fundingShortfall: number;
  deploymentRows: MoneyFlowRow[];
  saleFlow: SaleFlow | null;
  insightFinancials: GrannyFlatInsightFinancials | null;
}

interface GrannyFlatAnalyserProps {
  setActivePage: (page: string) => void;
  factFind: DraftFactFind;
  onBack: () => void;
  /** App-owned saved strategies — "Select Strategy" on an engine card saves here (the aged care rule). */
  setSavedStrategies?: React.Dispatch<React.SetStateAction<Array<Record<string, unknown>>>>;
  /** Persists the new-money profile answer to the client record (App owns the fact-find state). */
  setFactFind?: React.Dispatch<React.SetStateAction<DraftFactFind>>;
  onSoaGenerated?: (document: Record<string, unknown>) => void;
}

/** The lifetime annuity an engine candidate buys, in the shape the sentence and the warnings read. */
function gfAnnuityOf(strategy: GfStrategyView, factFind: DraftFactFind): GfInBriefAnnuity | null {
  const terms = deploymentAnnuityTerms(strategy.inputs?.deployment as ProceedsDeploymentValue | undefined, factFind?.adviceFor === 'couple');
  if (!terms) return null;
  const owner = terms.owner === 'partner' ? 'partner' : 'client';
  const ownerName = (owner === 'partner' ? factFind?.partner?.firstName : factFind?.client?.firstName) || 'you';
  return { amount: Number(terms.purchaseAmount) || 0, residualPercent: Number(terms.residualPercent) || 0, ownerName };
}

/** The age-and-health warning for a candidate's annuity — the aged care rule, applied here. */
function gfAnnuityHorizonWarningOf(strategy: GfStrategyView, factFind: DraftFactFind): string | null {
  const terms = deploymentAnnuityTerms(strategy.inputs?.deployment as ProceedsDeploymentValue | undefined, factFind?.adviceFor === 'couple');
  if (!terms) return null;
  const flag = annuityHorizonFlag({ factFind: factFind as unknown as HorizonFactFind, residualCapital: (Number(terms.residualPercent) || 0) / 100, owner: terms.owner });
  return flag?.warning ?? null;
}

/** The arrangement's facts for one candidate, read off its inputs exactly as the evaluator did. */
function gfMoveOf(strategy: GfStrategyView, hasHome: boolean, homeValue: number) {
  const cfg: Partial<GrannyFlatConfig> = strategy.inputs || {};
  const amountPaid = parseFloat(String(cfg.amountPaid ?? '')) || 0;
  const propertyTransferred = parseFloat(String(cfg.propertyTransferredValue ?? '')) || 0;
  const construction = parseFloat(String(cfg.constructionCost ?? '')) || 0;
  const entryContribution = amountPaid + propertyTransferred + construction;
  const selling = cfg.fundFrom === 'sell_home' && homeValue > 0;
  const keeping = hasHome && propertyTransferred === 0 && !selling;
  const rented = keeping && cfg.keptHomeUse === 'rented';
  const sellingCosts = parseFloat(String(cfg.sellingCosts ?? '')) || Math.round(homeValue * 0.025);
  const saleResidual = selling ? Math.max(0, homeValue - sellingCosts - amountPaid) : 0;
  const justMovingIn = /^gf-move-in-only/.test(String(strategy.id || ''));
  const home: 'kept' | 'rented' | 'sold' | 'transferred' | null = !hasHome ? null : selling ? 'sold' : propertyTransferred > 0 ? 'transferred' : rented ? 'rented' : 'kept';
  return {
    amountPaid, propertyTransferred, construction, entryContribution, isHomeowner: entryContribution > EXTRA_ALLOWABLE_AMOUNT,
    selling, keeping, rented, keptHomeRent: parseFloat(String(cfg.keptHomeRent ?? '')) || 0, sellingCosts, saleResidual, justMovingIn, home,
    deprivation: Number(strategy.deprivation) || 0,
  };
}

const GrannyFlatAnalyser = ({ setActivePage, factFind, onBack, setSavedStrategies, setFactFind, onSoaGenerated }: GrannyFlatAnalyserProps) => {
  const hasHome = (parseFloat(String(factFind?.home?.value ?? '')) || 0) > 0;
  const homeValue = parseFloat(String(factFind?.home?.value ?? '')) || 0;
  const todayIso = new Date().toISOString().slice(0, 10);
  const isCouple = factFind?.adviceFor === 'couple';
  const clientName = factFind?.client?.firstName || 'you';
  const household = isCouple && factFind?.partner?.firstName ? `${clientName} & ${factFind.partner.firstName}` : clientName;

  /*
   * THREE STEPS (owner, 24 Sep 2026: every module on the aged care spine). 1: the arrangement's
   * start date and what the right is worth. 2: where the client stands NOT entering it — the one
   * baseline. 3: the ways to set it up, in either mode, with the rail beside them.
   */
  const [step, setStep] = useState<AnalyserStep>(1);
  const goToStep = (next: AnalyserStep) => { setStep(next); scrollAppToTop(); };

  const [gfInputs, setGfInputs] = useState<GfInputs>({
    amountPaid: 0,
    transferHome: false,
    propertyTransferredValue: 0,
    constructionCost: 0,
    // Blank = starting now, as the other modules (owner, 2 Oct 2026): nothing is stored on the file
    // from here, so a date typed today would not be the date they see next week anyway. `gf` below
    // falls back to today, so the age factor, the pension rate and the five-year clock all run from
    // the day the arrangement is modelled.
    dateEntered: '',
    fundFrom: 'savings', // 'savings' | 'sell_home' (only when paying cash and not transferring the home)
    funding: {}, // chosen-asset funding map (assetId → amount) when paying cash from savings
    fundingCgtOverrides: {},
    deployment: {}, // when selling the home, how to deploy the residual (shares / IP / annuity); rest to bank
    justMovingIn: false, // no cash/asset transfer — simply moving in with family (no granny-flat interest, no gift)
    keptHomeUse: 'vacant', // when the former home is KEPT (savings-funded or just moving in): 'vacant' | 'rented'
    // Default the kept-home rent/expenses from the fact-find (home.likelyRent is WEEKLY, likelyExpenses is annual); overridable here.
    keptHomeRent: parseFloat(String(factFind?.home?.likelyRent ?? '')) || 0, // weekly gross rent when keptHomeUse='rented'
    keptHomeExpenses: parseFloat(String(factFind?.home?.likelyExpenses ?? '')) || 0, // annual property expenses when keptHomeUse='rented'
  });
  const [localLegacyYears, setLocalLegacyYears] = useState(10);
  const [showFamilyGuide, setShowFamilyGuide] = useState(false); // feedback #7: guide on its own page
  /** "Understanding your plan" — the plain-words recap on its own surface, as in Aged Care. */
  const [showGfExplanation, setShowGfExplanation] = useState(false);
  // '' until the user chooses — the mode choice is the FIRST act, matching the other modules.
  const [strategyMode, setStrategyMode] = useState<'manual' | 'optimised' | ''>('');
  const [seLoading, setSeLoading] = useState(false);
  const [gfSuggestions, setGfSuggestions] = useState<GrannyFlatSuggestionsResult | null>(null);
  /** `id` names the engine candidate behind it, so the year toggle can re-run that one. */
  const [seDeepDive, setSeDeepDive] = useState<{ projections: ProjectionYear[]; name?: string; id?: string } | null>(null);
  // "What could go wrong" for one engine card (StrategyRisksPanel).
  const [riskStrategy, setRiskStrategy] = useState<GfStrategyView | null>(null);
  /** The card being explained — set by "Explain this", never a selection (a selection is saved). */
  const [explainSeId, setExplainSeId] = useState<string | null>(null);
  const [showStayPutProjections, setShowStayPutProjections] = useState(false);
  const [showAllSeStrategies, setShowAllSeStrategies] = useState(false);
  /** Which goal's card is shown — aged care's chips (presentation only; the engine is not re-run). */
  const [optimisationFocus, setOptimisationFocus] = useState<GoalFocus>('legacy');

  /**
   * The Statement of Advice for the selected engine strategy (gfSoa.ts) — opened, downloaded,
   * registered. On the app's calendar-year setting the chosen plan and the benchmark are re-cut on
   * calendar years first (the same engine run the deep dive makes), so the document's rows match
   * what the client reads on screen (owner, 3 Oct 2026). If that run fails the financial-year rows stand.
   */
  const generateGfSoa = async (strategy: GfStrategyView) => {
    const periodMode = readPeriodMode();
    let picked = strategy;
    let all = gfStrategies;
    const rerun = gfSuggestions?.rerun;
    if (periodMode === 'calendar_year' && rerun) {
      const wanted = (rerun.candidates as { id?: unknown }[]).filter((c) => c?.id === strategy.id || c?.id === 'gf-stay-put');
      if (wanted.length) {
        try {
          const rows = (await calculationApi.evaluateGrannyFlatCandidates(factFind, wanted, rerun.legacyYears, rerun.date, 'calendar_year')) as Array<{ id?: string; projections?: ProjectionYear[] }>;
          const byId = new Map(rows.map((r) => [String(r.id), r.projections || []]));
          picked = { ...strategy, projections: byId.get(String(strategy.id)) ?? strategy.projections };
          all = gfStrategies.map((s) => (byId.has(String(s.id)) ? { ...s, projections: byId.get(String(s.id)) } : s));
        } catch { /* the financial-year rows stand */ }
      }
    }
    const snapshot = buildGrannyFlatSoaSnapshot({
      strategy: picked,
      all,
      factFind,
      legacyYears: Number(localLegacyYears) || 5,
      reasonableValue: Number(gfSuggestions?.reasonableValue) || 0,
      periodMode,
    });
    openModuleSoa(snapshot);
    onSoaGenerated?.({ id: strategy.id, name: strategy.name, type: 'granny_flat', source: 'suggestion_engine', strategy, soaSnapshot: snapshot });
  };
  /*
   * THE DEEP DIVE'S YEAR TOGGLE (owner, 23 Sep 2026). Re-cutting a candidate on the calendar year
   * means running it again through the ENGINE. The candidate, horizon and date come from the same
   * builder that ranked it (`rerun`), so nothing is reconstructed. Only offered where the candidate
   * is known.
   */
  const [modalPeriodMode, setModalPeriodMode] = useState<ProjectionsModalPeriodMode>(readPeriodMode);
  const [calendarYearProjections, setCalendarYearProjections] = useState<ProjectionYear[] | null>(null);
  const deepDiveCandidate = useMemo(() => {
    const rerun = gfSuggestions?.rerun;
    if (!rerun || !seDeepDive?.id) return null;
    const candidate = (rerun.candidates as { id?: unknown }[]).find((c) => c?.id === seDeepDive.id);
    return candidate ? { candidate, legacyYears: rerun.legacyYears, date: rerun.date } : null;
  }, [gfSuggestions, seDeepDive]);
  useEffect(() => {
    if (modalPeriodMode !== 'calendar_year' || !seDeepDive || !deepDiveCandidate) { setCalendarYearProjections(null); return undefined; }
    let cancelled = false;
    setCalendarYearProjections(null);
    calculationApi.evaluateGrannyFlatCandidates(
      factFind,
      [deepDiveCandidate.candidate],
      deepDiveCandidate.legacyYears,
      deepDiveCandidate.date,
      'calendar_year',
    ).then((rows: unknown) => {
      if (cancelled) return;
      const first = Array.isArray(rows) ? (rows[0] as { projections?: ProjectionYear[] } | undefined) : undefined;
      setCalendarYearProjections(first?.projections ?? []);
    });
    return () => { cancelled = true; };
  }, [modalPeriodMode, seDeepDive, deepDiveCandidate, factFind]);
  const [selectedSeId, setSelectedSeId] = useState<string | null>(null);
  /*
   * A real provider quote for the annuity candidates, and the draft being typed. Only the committed
   * value re-runs the field — typing a digit must not fire a run per keystroke.
   */
  const [gfAnnuityQuote, setGfAnnuityQuote] = useState<number | null>(null);
  const [gfAnnuityQuoteDraft, setGfAnnuityQuoteDraft] = useState<number | string>('');

  // "Just moving in" = no payment / transfer at all → no granny-flat interest, no gift, non-homeowner.
  const justMovingIn = !!gfInputs.justMovingIn;
  const propertyTransferred = justMovingIn ? 0 : (gfInputs.transferHome ? homeValue : (parseFloat(String(gfInputs.propertyTransferredValue)) || 0));
  const amountPaid = justMovingIn ? 0 : (parseFloat(String(gfInputs.amountPaid)) || 0);
  const constructionCost = justMovingIn ? 0 : (parseFloat(String(gfInputs.constructionCost)) || 0);
  // Construction is money you actually pay (to build the accommodation) — so it's consideration given, counts in
  // the homeowner test, and is funded from assets like the cash. It's offset by the dwelling you receive, so it
  // doesn't create a gift on its own.
  const entryContribution = amountPaid + propertyTransferred + constructionCost;
  const cashOutlay = amountPaid + constructionCost; // total money out (paid to family + paid to build)
  const hasArrangement = entryContribution > 0;
  const isHomeowner = entryContribution > EXTRA_ALLOWABLE_AMOUNT;

  // When the home is sold to fund the payment, this is the residual the client can deploy (rest to bank).
  const sellingHomeToFund = !justMovingIn && !gfInputs.transferHome && gfInputs.fundFrom === 'sell_home' && homeValue > 0;
  const saleResidual = sellingHomeToFund ? Math.max(0, homeValue - Math.round(homeValue * 0.025) - cashOutlay) : 0;
  // The former home is KEPT (not transferred, not sold) whenever there's a home and we're not doing either.
  const keepingHome = hasHome && propertyTransferred === 0 && !sellingHomeToFund;
  // Simulate once there's a real arrangement OR the client is just moving in (which still changes homeowner
  // status and makes any retained former home assessable).
  const shouldSimulate = hasArrangement || justMovingIn;
  const deployment = gfInputs.deployment; // always an object

  // Cash/construction outlay funded from savings (vs from selling the home). The client chooses which assets to
  // sell via the shared AssetFundingAllocator (feedback #6).
  const cashFromSavings = (gfInputs.transferHome || gfInputs.fundFrom === 'savings') ? cashOutlay : 0;
  // Hold the Financial Impact at baseline until the client has CHOSEN how to fund a savings-funded outlay — so
  // the panel doesn't jump as though the cash is already paid before they've picked the source.
  const fundingPending = cashFromSavings > 0 && Object.keys(gfInputs.funding || {}).length === 0;
  const funding = gfInputs.funding; // always an object
  const fundingCgtOverrides = gfInputs.fundingCgtOverrides;

  const gf = useMemo(() => ({
    amountPaid,
    propertyTransferredValue: propertyTransferred,
    constructionCost: justMovingIn ? 0 : (parseFloat(String(gfInputs.constructionCost)) || 0),
    dateEntered: gfInputs.dateEntered || todayIso,
    fundFrom: (justMovingIn || gfInputs.transferHome) ? 'savings' : gfInputs.fundFrom,
    deployment: sellingHomeToFund ? deployment : undefined,
    funding: cashFromSavings > 0 ? funding : undefined,
    fundingCgtOverrides,
    sellingCosts: undefined,
    // When the home is kept, how it's used drives whether the (now-assessable) former home also earns rent.
    keptHomeUse: keepingHome ? gfInputs.keptHomeUse : 'vacant',
    keptHomeRent: parseFloat(String(gfInputs.keptHomeRent)) || 0,
    keptHomeExpenses: parseFloat(String(gfInputs.keptHomeExpenses)) || 0,
  }), [amountPaid, propertyTransferred, justMovingIn, gfInputs.constructionCost, gfInputs.dateEntered, gfInputs.transferHome, gfInputs.fundFrom, sellingHomeToFund, deployment, cashFromSavings, funding, fundingCgtOverrides, todayIso, keepingHome, gfInputs.keptHomeUse, gfInputs.keptHomeRent, gfInputs.keptHomeExpenses]);

  /*
   * TODAY'S POSITION — the standalone assessment of the profile as it stands, run always: it is
   * step 2's four numbers, the rail's "before", and what the engine's rail holds.
   */
  const [todayFinancials, setTodayFinancials] = useState<Financials | null>(null);
  useEffect(() => {
    let cancelled = false;
    (calculationApi.calculateFinancials(factFind) as Promise<Financials>).then((r) => { if (!cancelled) setTodayFinancials(r); }).catch(() => { if (!cancelled) setTodayFinancials(null); });
    return () => { cancelled = true; };
  }, [factFind]);

  // Simulate once there's a real arrangement OR the client is just moving in. Otherwise show no change
  // (before == after) so nothing moves before the client has entered anything.
  const [{ before, after: afterRaw, simulated }, setBeforeAfter] = useState<GfBeforeAfter>({ before: {}, after: {}, simulated: factFind });
  useEffect(() => {
    let cancelled = false;
    (async () => {
      let resolved: GfBeforeAfter;
      if (shouldSimulate) {
        resolved = await (calculationApi.getGrannyFlatBeforeAfter(factFind, gf) as Promise<GfBeforeAfter>);
      } else {
        const b = (await calculationApi.calculateFinancials(factFind)) as Financials;
        resolved = { before: b, after: b, simulated: factFind };
      }
      if (!cancelled) setBeforeAfter(resolved);
    })();
    return () => { cancelled = true; };
  }, [factFind, gf, shouldSimulate]);

  /**
   * The debt strategy, shared with the Aged Care module: clear some or all of the client's debts as
   * part of the plan. Applied at the origin too — one transformation feeds the after-position, the
   * projections and the panels, which is what keeps them agreeing with each other.
   */
  const [debtPayments, setDebtPayments] = useState<Record<string, number>>({});
  const [debtFunding, setDebtFunding] = useState<Record<string, number>>({});
  const [debtRepaymentBasis, setDebtRepaymentBasis] = useState<'keep_repayment' | 'lower_repayment'>('keep_repayment');
  const debtPayoffPlan = useMemo<DebtPayoff | null>(() => {
    const payments = Object.fromEntries(Object.entries(debtPayments).filter(([, amount]) => (Number(amount) || 0) > 0));
    if (Object.keys(payments).length === 0) return null;
    const funding = Object.fromEntries(Object.entries(debtFunding).filter(([, amount]) => (Number(amount) || 0) > 0));
    return { payments, funding, repaymentBasis: debtRepaymentBasis };
  }, [debtPayments, debtFunding, debtRepaymentBasis]);
  /**
   * The arrangement's start date becomes a REAL deferral when it is in the future: the engine
   * establishes the arrangement on the day, against live balances, and the years before it are the
   * client's current life. Today or the past keeps the long-standing behaviour (arrangement now).
   */
  const futureGfDate = gf.dateEntered && gf.dateEntered > todayIso ? gf.dateEntered : '';

  const simulatedForModelling = useMemo(
    () => (debtPayoffPlan ? (applyDebtPayoffToFactFind(simulated as never, debtPayoffPlan).factFind as unknown as DraftFactFind) : simulated),
    [simulated, debtPayoffPlan],
  );
  // The after-position with the payoff in it. Recomputed locally only when a plan exists, so the
  // no-debt path stays exactly the server round-trip it always was.
  const [afterWithDebt, setAfterWithDebt] = useState<Financials | null>(null);
  useEffect(() => {
    if (!debtPayoffPlan || !shouldSimulate) { setAfterWithDebt(null); return undefined; }
    let cancelled = false;
    (calculationApi.calculateFinancials(simulatedForModelling) as Promise<Financials>).then((r) => { if (!cancelled) setAfterWithDebt(r); });
    return () => { cancelled = true; };
  }, [simulatedForModelling, debtPayoffPlan, shouldSimulate]);
  const after = afterWithDebt ?? afterRaw;
  const depResult = useMemo<DepResult | null>(
    () => (hasArrangement ? getGrannyFlatDeprivationFromFactFind(simulated as unknown as FactFind, PENSION_RATES.couple) : null),
    [simulated, hasArrangement],
  );
  const [projections, setProjections] = useState<ProjectionYear[]>([]);
  useEffect(() => {
    if (!shouldSimulate) { setProjections([]); return undefined; }
    let cancelled = false;
    const projectionsPromise = futureGfDate
      ? (calculationApi.runGrannyFlatProjections(factFind, {
            legacyYears: localLegacyYears,
            grannyFlatEvent: { date: futureGfDate, gf },
            ...(debtPayoffPlan ? { debtPayoff: debtPayoffPlan } : {}),
        }) as Promise<ProjectionYear[]>)
      : (calculationApi.runGrannyFlatProjections(simulatedForModelling, { legacyYears: localLegacyYears }) as Promise<ProjectionYear[]>);
    projectionsPromise.then((proj) => {
      if (!cancelled) setProjections(proj);
    });
    return () => { cancelled = true; };
  }, [simulatedForModelling, shouldSimulate, localLegacyYears, futureGfDate, factFind, gf, debtPayoffPlan]);
  const legacyEstimate = projections.length > 0
    ? (projections[localLegacyYears - 1]?.netWorth ?? projections[projections.length - 1]?.netWorth ?? after.netWorth)
    : after.netWorth;

  const manualDeploymentRows = describeResidualDeployment(gfInputs.deployment as ResidualDeployment);

  const reasonableValue = depResult?.reasonableValue ?? 0;
  const interestValue = depResult?.interestValue ?? 0;
  const deprivation = depResult?.deprivation ?? 0;

  /*
   * CENTRELINK'S REASONABLE VALUE AT THE START DATE, whether or not anything has been entered —
   * the hero's tile and the setup's anchor. The engine's own figure (the same probe the
   * generator makes), so the three surfaces cannot quote three values.
   */
  const [reasonableValueToday, setReasonableValueToday] = useState<number | null>(null);
  useEffect(() => {
    let cancelled = false;
    getGrannyFlatReasonableValueForClient(factFind, gfInputs.dateEntered || todayIso)
      .then((v) => { if (!cancelled) setReasonableValueToday(v); })
      .catch(() => { if (!cancelled) setReasonableValueToday(null); });
    return () => { cancelled = true; };
  }, [factFind, gfInputs.dateEntered, todayIso]);

  // How Centrelink's assessed value is calculated, in words (hover tooltip).
  const assessedValueTooltip = depResult
    ? `Centrelink values your lifetime right as: the combined couple Age Pension rate (${formatCurrency(depResult.combinedPartneredPensionRate)}/yr)${depResult.ageNextBirthday != null ? ` × a conversion factor of ${Number(depResult.conversionFactor).toFixed(2)} (set by your age next birthday, ${depResult.ageNextBirthday}${factFind?.adviceFor === 'couple' ? ', using the younger of you' : ''})` : ' × an age-based conversion factor'} = ${formatCurrency(reasonableValue)}.${depResult.providedValue > reasonableValue ? ` Because the property/home you transfer (${formatCurrency(depResult.providedValue)}) is worth more, that higher value is used instead.` : ''} This is the most you can give without any of it counting as a gift.`
    : 'Centrelink values your lifetime right as the combined couple Age Pension rate × an age-based conversion factor (younger of a couple, age next birthday).';

  const insightFinancials = useMemo<GrannyFlatInsightFinancials>(() => ({
    agePensionBefore: before?.socialSecurity ?? 0,
    agePensionAfter: after?.socialSecurity ?? 0,
    cashflowAfter: projections[0]?.netCashflow ?? after?.cashFlow ?? 0,
    legacyEstimate,
  }), [before, after, projections, legacyEstimate]);

  const setField = <K extends keyof GfInputs>(field: K, value: GfInputs[K]) => setGfInputs((prev) => ({ ...prev, [field]: value }));

  /*
   * THE BENCHMARK: this client's position over the horizon if they simply do not enter the
   * arrangement. Kept WHOLE: the stay-put projection is the position step 2 shows, the runway the
   * rail carries until a plan is named, and what "See the projections" opens in that state — one
   * run, read everywhere not entering is quoted. The same run the engine's own baseline makes.
   */
  const [stayPutProjections, setStayPutProjections] = useState<ProjectionYear[]>([]);
  useEffect(() => {
    let cancelled = false;
    // The granny flat projection route with NO arrangement: the client's life as it is, over the
    // horizon — the same run the engine's do-not-proceed candidate makes.
    (calculationApi.runGrannyFlatProjections(factFind, { legacyYears: localLegacyYears }) as Promise<ProjectionYear[]>)
      .then((rows) => { if (!cancelled) setStayPutProjections(rows || []); })
      .catch(() => { if (!cancelled) setStayPutProjections([]); });
    return () => { cancelled = true; };
  }, [factFind, localLegacyYears]);
  const manualBaselineLegacy: number | null = useMemo(() => {
    const last = stayPutProjections[localLegacyYears - 1] ?? stayPutProjections[stayPutProjections.length - 1];
    return typeof last?.netWorth === 'number' ? last.netWorth : null;
  }, [stayPutProjections, localLegacyYears]);
  const stayPutRunway = useMemo(
    () => (stayPutProjections.length ? planRunwayCopy(stayPutProjections as never, Number(localLegacyYears) || 5) : null),
    [stayPutProjections, localLegacyYears],
  );

  /*
   * THE STRATEGY ANALYSIS SETUP (owner, 2 Oct 2026) — what they will pay, what happens to the
   * home, the annuity share, the reserve. Seeded from the client record; opened the first time the
   * engine is chosen; "Adjust setup" reopens.
   */
  const [gfSetup, setGfSetup] = useState<GfEngineSetup>(() => gfEngineSetupFromGoals(factFind));
  const [gfSetupOpen, setGfSetupOpen] = useState(false);
  const [gfSetupSeen, setGfSetupSeen] = useState(false);

  // The just-in-time "how would new money be invested?" gate — asked in the setup first; this is
  // the fallback when that was cancelled. Answering saves to the profile (asked once, ever).
  const [newMoneySkipped, setNewMoneySkipped] = useState(false);

  /*
   * THE ANNUITY WILLINGNESS QUESTION (owner, 25 Sep 2026), asked in the setup and, where that was
   * cancelled, here. "No" removes every annuity strategy from this and every other module.
   */
  const [annuityPrefSkipped, setAnnuityPrefSkipped] = useState(false);
  const needsAnnuityAnswer = !annuityPreferenceOf(factFind as never).answered && !annuityPrefSkipped;
  const answerAnnuityPreference = (appetite: AnnuityAppetite, residual: AnnuityResidual) => {
    if (setFactFind) setFactFind((prev) => ({ ...prev, goals: { ...(prev.goals || {}), annuityAppetite: appetite, annuityResidual: residual } }));
    else setAnnuityPrefSkipped(true);
    setGfSetup((prev) => ({ ...prev, annuityAppetite: appetite, annuityResidual: residual }));
  };

  const needsNewMoneyAnswer = strategyMode === 'optimised' && hasHome && !hasAnsweredNewMoneyProfile(factFind) && !newMoneySkipped;
  const answerNewMoneyProfile = (id: InvestmentProfileId) => {
    if (setFactFind) {
      setFactFind((prev) => ({ ...prev, goals: { ...(prev.goals || {}), newMoneyInvestmentProfile: id } }));
    } else {
      setNewMoneySkipped(true);
    }
  };

  // The Transfer Balance Cap question: only when a sale route would COMMENCE a pension and a
  // person's balances trip the $1.6m screen (transferBalanceCap.ts).
  const [tbcSkipped, setTbcSkipped] = useState(false);
  const seRunCommencesPension = useMemo(() => {
    if (!hasHome) return false;
    const room = nonConcessionalCapacityForHousehold(factFind as unknown as FactFind);
    return room.total > 0 && room.preferred == null && (gfSetup.homeRoute === 'sell' || gfSetup.homeRoute === 'all' || gfSetup.homeRoute == null);
  }, [factFind, hasHome, gfSetup.homeRoute]);
  const tbcPersonNeedingAnswer: 'client' | 'partner' | null = !tbcSkipped && seRunCommencesPension
    ? (tbcQuestionNeededFor(factFind as never, 'client') ? 'client'
      : (isCouple && tbcQuestionNeededFor(factFind as never, 'partner') ? 'partner' : null))
    : null;
  const needsTbcAnswer = strategyMode === 'optimised' && tbcPersonNeedingAnswer !== null;
  const answerTbc = (amount: number) => {
    if (setFactFind && tbcPersonNeedingAnswer) {
      const key = tbcPersonNeedingAnswer;
      setFactFind((prev) => ({ ...prev, [key]: { ...(prev[key] || {}), transferBalanceUsed: amount } }));
    } else {
      setTbcSkipped(true);
    }
  };

  // Suggestion Engine: paint loader, then compute, then hold to a minimum (shared pattern). The
  // engine waits while the setup is open — its answers are inputs to the run.
  useEffect(() => {
    if (strategyMode !== 'optimised') return undefined;
    if (gfSetupOpen) { setSeLoading(false); return undefined; }
    if (needsNewMoneyAnswer || needsTbcAnswer) { setSeLoading(false); return undefined; }
    setSeLoading(true);
    const startedAt = Date.now();
    const t = setTimeout(async () => {
      let result: GrannyFlatSuggestionsResult | null = null;
      try { result = await buildGrannyFlatSuggestions(factFind, { legacyYears: localLegacyYears, dateEntered: gf.dateEntered, currentInputs: gfInputs, setup: gfSetup, annuityIncomeOverride: gfAnnuityQuote }); } catch { result = null; }
      setGfSuggestions(result);
      setTimeout(() => setSeLoading(false), Math.max(0, 2000 - (Date.now() - startedAt)));
    }, 60);
    return () => clearTimeout(t);
  // gfAnnuityQuote: a committed provider quote re-ranks the whole field, because a different rate
  // can change which strategy wins.
  }, [strategyMode, factFind, localLegacyYears, gf.dateEntered, gfInputs, needsNewMoneyAnswer, needsTbcAnswer, gfSetup, gfSetupOpen, gfAnnuityQuote]);

  const gfStrategies = useMemo(
    () => (gfSuggestions ? enrichStrategiesWithRecommendationLabels(gfSuggestions.strategies, gfSuggestions.recommended) : []) as GfStrategyView[],
    [gfSuggestions],
  );
  const gfStayPut = useMemo(() => (gfSuggestions?.stayPut as GfStrategyView | null | undefined) ?? null, [gfSuggestions]);
  const isGfBaseline = (s: GfStrategyView) => s.id === 'gf-stay-put';

  // Nothing is selected for the reader (aged care's rule): a card is selected by choosing it. Cleared outside engine mode.
  useEffect(() => {
    if (strategyMode !== 'optimised' && selectedSeId !== null) setSelectedSeId(null);
  }, [strategyMode, selectedSeId]);

  /*
   * GOAL LEADERS, with the aged care horizon rule: a candidate whose annuity needs more years than
   * this client's age and health point to is set aside from the leadership — never hidden.
   */
  const gfRecommended = useMemo<RecommendedStrategyEntry[]>(() => {
    if (!gfSuggestions) return [];
    const movers = gfStrategies.filter((s) => !isGfBaseline(s));
    const eligible = movers.filter((s) => gfAnnuityHorizonWarningOf(s, factFind) == null);
    if (eligible.length === movers.length) return gfSuggestions.recommended;
    return buildRecommendedOptimalStrategies(eligible, gfStayPut);
  }, [gfSuggestions, gfStrategies, gfStayPut, factFind]);
  const gfGoalLeaders: GfStrategyView[] = useMemo(() => mergeGoalLeaderStrategies(gfRecommended) as GfStrategyView[], [gfRecommended]);
  const coveredSameAsEstate = useMemo(() => coveredIsBestEstate(gfRecommended), [gfRecommended]);
  const coveredNoteFor = (s: GfStrategyView): string | null => {
    const v = gfRecommended.find((r) => r.label === CASHFLOW_COVERED_LABEL && r.strategy?.name === s.name)?.cashflowCovered;
    return v ? cashflowCoveredNote(v, Number(localLegacyYears) || 5, formatCurrency) : null;
  };
  const gfSetAside = useMemo(() => {
    if (!gfSuggestions) return [] as Array<{ strategy: GfStrategyView; labels: string[]; warning: string }>;
    return (mergeGoalLeaderStrategies(gfSuggestions.recommended) as GfStrategyView[])
      .map((s) => ({ strategy: s, labels: s.recommendationLabels || [], warning: gfAnnuityHorizonWarningOf(s, factFind) || '' }))
      .filter((x) => x.warning);
  }, [gfSuggestions, factFind]);
  const explainStrategy: GfStrategyView | null = useMemo(
    () => (strategyMode === 'optimised' ? (gfStrategies.find((s) => s.id === explainSeId) || null) : null),
    [strategyMode, gfStrategies, explainSeId],
  );
  /** Which strategy CARDS appear, and what each is badged as — the shared rule (engineGoalCards.ts). */
  const seCards = useMemo(() => buildGoalCards<GfStrategyView>({
    strategies: gfStrategies,
    goalLeaders: gfGoalLeaders,
    baseline: gfStayPut,
    isBaseline: isGfBaseline,
    selectedId: selectedSeId,
    baselineWord: 'not entering',
    formatCurrency,
  }), [gfStrategies, gfGoalLeaders, gfStayPut, selectedSeId]);
  const visibleSeCards = useMemo(() => visibleGoalCards(seCards, optimisationFocus, coveredSameAsEstate), [seCards, optimisationFocus, coveredSameAsEstate]);
  const labelMap: Record<string, string> = { ...GOAL_CHIP_LABELS };

  /**
   * "Select Strategy" on an engine card SAVES it, exactly as it does in Aged Care — the saved list
   * is what the Dashboard and the advice document read.
   */
  const selectEngineStrategy = (strategy: GfStrategyView) => {
    setSelectedSeId(strategy.id ?? null);
    if (!setSavedStrategies) return;
    const recommended = gfGoalLeaders.map((l) => ({ labels: l.recommendationLabels || [], name: l.name }));
    setSavedStrategies((prev) => [
      ...(prev || []),
      {
        id: `gf-se-${Date.now()}`,
        name: strategy.name || 'Suggested granny flat arrangement',
        type: 'granny_flat',
        source: 'suggestion_engine',
        inputs: {
          gf: { ...(strategy.inputs || {}) },
          setup: { ...gfSetup },
          reasonableValue: Number(gfSuggestions?.reasonableValue) || 0,
          source: 'suggestion_engine',
          seStrategyName: strategy.name ?? null,
          recommendedOptimalStrategies: recommended,
          clientSelectedIsAlternative: recommended.length > 0 && !recommended.some((r) => r.name === strategy.name),
        },
        outputs: {
          agePension: strategy.snapshot?.pension ?? 0,
          cashFlow: strategy.snapshot?.netCashflow ?? 0,
          legacyEstimate: strategy.snapshot?.legacyValue ?? 0,
          deprivation: Number(strategy.deprivation) || 0,
          netWorthSchedule: buildNetWorthSchedule((strategy.projections || []) as never),
        },
        createdAt: new Date().toISOString(),
      },
    ]);
  };

  // The explanation of an engine card is built from ITS before/after — never from Manual Control's
  // inputs (which would re-trigger the engine) and never from the rail, which holds not entering.
  const [explainView, setExplainView] = useState<SeView | null>(null);
  useEffect(() => {
    if (!explainStrategy) { setExplainView(null); return undefined; }
    let cancelled = false;
    const cfg: Partial<GrannyFlatConfig> = explainStrategy.inputs || {};
    (calculationApi.getGrannyFlatBeforeAfter(factFind, cfg) as Promise<GfBeforeAfter>).then(({ before: sBefore, after: sAfter, simulated: sSim }) => {
      if (cancelled) return;
      const dep = getGrannyFlatDeprivationFromFactFind(sSim as unknown as FactFind, PENSION_RATES.couple);
      const sDeploymentRows = describeResidualDeployment(cfg.deployment as ResidualDeployment | undefined);
      const proj = explainStrategy.projections || [];
      const m = gfMoveOf(explainStrategy, hasHome, homeValue);
      const sLegacy = proj.length
        ? (proj[localLegacyYears - 1]?.netWorth ?? proj[proj.length - 1]?.netWorth ?? sAfter?.netWorth ?? 0)
        : (sAfter?.netWorth ?? 0);
      setExplainView({
        name: explainStrategy.name,
        before: sBefore, after: sAfter, projections: proj, depResult: dep,
        hasArrangement: m.entryContribution > 0, justMovingIn: m.justMovingIn,
        amountPaid: m.amountPaid, propertyTransferred: m.propertyTransferred, entryContribution: m.entryContribution,
        isHomeowner: m.isHomeowner,
        keepingHome: m.keeping, keptHomeRented: m.rented,
        formerHomeValue: m.keeping ? homeValue : 0,
        reasonableValue: dep?.reasonableValue ?? 0, interestValue: dep?.interestValue ?? 0, deprivation: dep?.deprivation ?? 0,
        legacyEstimate: sLegacy,
        fundingRows: explainStrategy.fundingBreakdown || [],
        fundingCgt: explainStrategy.fundingCgt || 0,
        fundingShortfall: explainStrategy.fundingShortfall || 0,
        deploymentRows: sDeploymentRows,
        saleFlow: m.selling ? { homeValue, sellingCosts: m.sellingCosts, amountPaid: m.amountPaid, residual: m.saleResidual } : null,
        insightFinancials: {
          agePensionBefore: sBefore?.socialSecurity ?? 0,
          agePensionAfter: sAfter?.socialSecurity ?? 0,
          cashflowAfter: proj[0]?.netCashflow ?? proj[0]?.cashFlow ?? sAfter?.cashFlow ?? 0,
          legacyEstimate: sLegacy,
        },
      });
    });
    return () => { cancelled = true; };
  }, [explainStrategy, factFind, hasHome, homeValue, localLegacyYears]);

  // Manual: while funding is pending (savings outlay not yet allocated), hold the Financial Impact at baseline.
  const manualImpactReady = shouldSimulate && !fundingPending;
  const fundingMeta = simulated?.__grannyFlatFundingMeta as GrannyFlatFundingMeta | undefined;
  // Manual Control's model: the narrative, the rail and the explanation read one object.
  const panelModel = useMemo<PanelModel>(() => ({
    name: null,
    before, after: manualImpactReady ? after : before, simulated, depResult,
    hasArrangement, justMovingIn,
    amountPaid, propertyTransferred, entryContribution, isHomeowner,
    keepingHome: keepingHome && shouldSimulate, keptHomeRented: keepingHome && gfInputs.keptHomeUse === 'rented',
    formerHomeValue: keepingHome ? homeValue : 0,
    reasonableValue, interestValue, deprivation,
    legacyEstimate: manualImpactReady ? legacyEstimate : (before?.netWorth ?? 0),
    projections: manualImpactReady ? projections : [],
    fundingRows: manualImpactReady ? (fundingMeta?.breakdown || []) : [],
    fundingCgt: manualImpactReady ? (fundingMeta?.cgt || 0) : 0,
    fundingShortfall: manualImpactReady ? (fundingMeta?.shortfall || 0) : 0,
    deploymentRows: manualImpactReady ? manualDeploymentRows : [],
    saleFlow: (manualImpactReady && sellingHomeToFund) ? { homeValue, sellingCosts: Math.round(homeValue * 0.025), amountPaid: cashOutlay, residual: saleResidual } : null,
    insightFinancials: manualImpactReady ? insightFinancials : null,
  }), [before, after, manualImpactReady, simulated, depResult, hasArrangement, justMovingIn, amountPaid, propertyTransferred, entryContribution, isHomeowner, keepingHome, shouldSimulate, gfInputs.keptHomeUse, homeValue, reasonableValue, interestValue, deprivation, legacyEstimate, projections, fundingMeta, manualDeploymentRows, sellingHomeToFund, cashOutlay, saleResidual, insightFinancials]);

  /*
   * ── THE PLAN SUMMARY RAIL ────────────────────────────────────────────────────────────────────
   * THE RAIL HOLDS NOT ENTERING THE ARRANGEMENT — the one baseline — until Manual Control names a
   * plan; in engine mode always: each card carries its own figures, measured against this rail.
   */
  const gfPlanSummaryName = strategyMode === 'manual' && manualImpactReady ? 'Your granny flat arrangement' : null;
  const railHoldsStayPut = !gfPlanSummaryName;
  const railProjections: ProjectionYear[] = railHoldsStayPut ? stayPutProjections : panelModel.projections;
  const gfRunway = useMemo(
    () => (railProjections.length ? planRunwayCopy(railProjections as never, Number(localLegacyYears) || 5) : null),
    [railProjections, localLegacyYears],
  );
  const railBefore = (todayFinancials ?? before) as FinancialPosition;

  /** The panel's "In brief": the SAME sentence template every engine card carries (gfInBrief.tsx). */
  const gfInBrief = useMemo<React.ReactNode | null>(() => {
    if (!gfPlanSummaryName) return null;
    const cashBefore = panelModel.before?.cashFlow ?? 0;
    const manualTerms = deploymentAnnuityTerms(deployment, isCouple);
    return (
      <GfInBrief
        entryContribution={panelModel.entryContribution}
        propertyTransferred={panelModel.propertyTransferred}
        justMovingIn={panelModel.justMovingIn}
        deprivation={panelModel.deprivation}
        home={!hasHome ? null : panelModel.saleFlow ? 'sold' : panelModel.propertyTransferred > 0 ? 'transferred' : panelModel.keptHomeRented ? 'rented' : 'kept'}
        keptHomeRentPerWeek={parseFloat(String(gfInputs.keptHomeRent)) || 0}
        saleResidual={panelModel.saleFlow?.residual ?? 0}
        pensionBefore={panelModel.before?.socialSecurity ?? 0}
        pensionAfter={panelModel.after?.socialSecurity ?? 0}
        cashBefore={cashBefore}
        cashAfter={panelModel.projections[0]?.netCashflow ?? panelModel.after?.cashFlow ?? cashBefore}
        planLegacy={panelModel.legacyEstimate ?? null}
        baseLegacy={manualBaselineLegacy}
        legacyYears={Number(localLegacyYears) || 0}
        annuity={manualTerms ? { amount: Number(manualTerms.purchaseAmount) || 0, residualPercent: Number(manualTerms.residualPercent) || 0, ownerName: (manualTerms.owner === 'partner' ? factFind?.partner?.firstName : factFind?.client?.firstName) || 'you' } : null}
      />
    );
  }, [gfPlanSummaryName, panelModel, hasHome, gfInputs.keptHomeRent, manualBaselineLegacy, localLegacyYears, deployment, isCouple, factFind]);

  /** The plain-words recap, read inside "Understanding your plan" rather than inline. */
  const recapModel = explainView ?? panelModel;
  const gfRecapNode = (
    <GrannyFlatPlanRecap
      model={recapModel}
      firstName={factFind?.client?.firstName}
      baselineLegacy={manualBaselineLegacy}
      legacyYears={localLegacyYears}
    />
  );

  /** The engine card's money flow: the home (sold / signed over) → the right → a nil-residual annuity. */
  const flowFor = (s: GfStrategyView): MoneyFlowStep[] => {
    const m = gfMoveOf(s, hasHome, homeValue);
    const annuity = gfAnnuityOf(s, factFind);
    const steps: MoneyFlowStep[] = [];
    if (m.selling) steps.push({ amount: homeValue, label: 'home sold', sub: `less ${formatCurrency(m.sellingCosts)} selling costs`, tone: 'sold' });
    if (m.propertyTransferred > 0) steps.push({ amount: m.propertyTransferred, label: 'home signed over', sub: 'for the right to live there', tone: 'sold' });
    if (m.construction > 0) steps.push({ amount: m.construction, label: 'paid to build', sub: 'taken at face value · no gift', tone: 'good' });
    if (m.amountPaid > 0) steps.push({ amount: m.amountPaid, label: m.selling ? 'paid for the right' : m.construction > 0 ? 'cash on top' : 'paid from savings', sub: m.deprivation > 0 ? `${formatCurrency(m.deprivation)} of it counts as a gift` : 'no gift · the right is exempt', tone: m.deprivation > 0 ? 'warn' : 'good' });
    if (m.rented) steps.push({ amount: m.keptHomeRent * 52, label: 'home kept, rented out', sub: 'rent a year, assessable', tone: 'good' });
    if (annuity && Math.round(annuity.residualPercent) === 0) steps.push({ amount: annuity.amount, label: 'lifetime annuity', sub: 'nothing returned on death', tone: 'warn' });
    return steps;
  };

  const stepDefs = [
    { title: 'The arrangement', sub: 'When it starts, and what the right is worth' },
    { title: `Where ${clientName} ${factFind?.client?.firstName ? 'stands' : 'stand'} today`, sub: 'Four numbers, not entering it' },
    { title: 'Ways to set it up', sub: 'Recommended plans, or build your own' },
  ] as const;

  if (showFamilyGuide) {
    return (
      <GrannyFlatFamilyGuide
        factFind={factFind}
        setFactFind={setFactFind}
        figures={hasArrangement ? { entryContribution, interestValue, deprivation, isHomeowner, dateEntered: gfInputs.dateEntered } : null}
        assessedValueTooltip={assessedValueTooltip}
        onBack={() => setShowFamilyGuide(false)}
        backLabel="Back to the calculator"
        onBackToDashboard={() => setActivePage('Dashboard')}
      />
    );
  }

  return (
    <div className="max-w-[1520px] mx-auto">

      <div className="flex items-center gap-2 mb-4 -ml-2">
        <Button variant="ghost" size="sm" icon={ChevronLeft} onClick={onBack}>Back</Button>
        <button onClick={() => setActivePage('Dashboard')} className="text-blue-600 hover:text-blue-800 font-medium text-sm">Back to Dashboard</button>
      </div>
      <h1 className="text-4xl font-bold text-gray-900 mb-2"><GradientText>Granny Flat Calculator</GradientText></h1>
      <p className="text-lg text-slate-700 leading-relaxed">
        Three short steps: the arrangement, where {household} {isCouple && factFind?.partner?.firstName ? 'stand' : (factFind?.client?.firstName ? 'stands' : 'stand')} today without it, and the ways to set it up.
      </p>
      <AnalyserSteps step={step} onStep={goToStep} steps={stepDefs} ariaLabel="Granny flat analyser steps" />

      {step === 1 && (
        <div className="mt-8 space-y-6">
          <Card>
            <h3 className="text-xl font-bold text-gray-800 mb-4">The arrangement</h3>
            <div className="space-y-4">
              <div className="flex flex-col md:flex-row md:items-end gap-4 p-3 rounded-lg border border-slate-200 bg-slate-50/60">
                <div className="flex-1">
                  <label htmlFor="gf-event-date" className="block text-sm font-semibold text-gray-800 flex items-center gap-1"><CalendarDays size={16} /> When will the arrangement start?</label>
                  <p className="text-xs text-gray-600 mt-1">
                    Leave this blank to model it from today — the usual case, and the right choice if you&apos;re
                    still deciding. Choose a future date and we show life as it is until then, with the arrangement
                    beginning on the day you pick. The date sets the age used to value the right, the pension rate
                    it is valued at, and starts the 5-year gifting clock.
                  </p>
                </div>
                <div>
                  <input id="gf-event-date" type="date" value={gfInputs.dateEntered} onChange={(e) => setField('dateEntered', e.target.value)} className="px-4 py-2 bg-white border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500" />
                  {gfInputs.dateEntered !== '' && gfInputs.dateEntered < todayIso && (
                    <p className="text-xs text-amber-700 mt-1">A past date models as &ldquo;starting now&rdquo;. An arrangement that already exists belongs in your profile instead.</p>
                  )}
                </div>
              </div>
              <div className="rounded-lg border border-slate-200 border-l-4 border-l-blue-500 bg-slate-50 p-3 flex items-center justify-between gap-3">
                <span className="text-sm font-medium text-gray-800">Centrelink&apos;s reasonable value of a lifetime right {gfInputs.dateEntered && gfInputs.dateEntered > todayIso ? 'at that date' : 'today'} <InfoIcon text="The combined couple Age Pension rate × a conversion factor set by age next birthday (the younger of a couple). Pay cash up to this for the right and nothing counts as a gift; pay more and the excess is counted for five years. Signing over your home or paying for the build is taken at face value instead, so none of that is a gift." /></span>
                <span className="text-lg font-bold text-blue-700 tabular-nums">{reasonableValueToday != null ? formatCurrency(Math.round(reasonableValueToday)) : '…'}</span>
              </div>
            </div>
          </Card>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <button type="button" onClick={onBack} className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-blue-700 hover:text-blue-900">
              <BookOpen size={15} /> Read the overview of granny flat arrangements
            </button>
            <button
              type="button"
              onClick={() => goToStep(2)}
              className="inline-flex h-11 items-center gap-2 rounded-lg bg-gradient-to-r from-blue-600 to-teal-500 px-5 text-[14px] font-semibold text-white shadow-sm hover:from-blue-700 hover:to-teal-600"
            >
              See where {clientName} {factFind?.client?.firstName ? 'stands' : 'stand'} today <ArrowRight size={16} />
            </button>
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="mt-8">
          <GfStayPutHero
            factFind={factFind}
            todayFinancials={todayFinancials as { socialSecurity?: number; cashFlow?: number; netWorth?: number; socialSecurityBreakdown?: unknown } | null}
            stayPutLegacy={manualBaselineLegacy}
            runway={stayPutRunway}
            legacyYears={localLegacyYears}
            reasonableValue={reasonableValueToday}
            homeValue={homeValue}
            onLegacyYearsChange={setLocalLegacyYears}
            onBack={() => goToStep(1)}
            onNext={() => goToStep(3)}
            onReadGuide={onBack}
            onAssumptions={() => setActivePage('AssumptionsPage')}
            onShowProjections={() => setShowStayPutProjections(true)}
          />
        </div>
      )}

      {step === 3 && (
      <div className="mt-8 grid grid-cols-1 xl:grid-cols-5 gap-8 items-start">
        <div className="xl:col-span-3 space-y-6">
          <Card>
            <ModeChooser
              heading="How do you want to model your options?"
              value={strategyMode}
              onSelect={(id) => {
                const next = id as 'manual' | 'optimised';
                setStrategyMode(next);
                if (next === 'manual') { setGfSuggestions(null); setSelectedSeId(null); }
                // The setup opens once per visit on choosing the engine; "Adjust setup" reopens it.
                if (next === 'optimised' && !gfSetupSeen) setGfSetupOpen(true);
              }}
              options={[
                {
                  id: 'manual',
                  title: 'Manual Control',
                  blurb: "Enter what's paid or transferred for the right to live there, how it's funded, and what happens to the former home.",
                  chip: 'Advisers & confident users',
                },
                {
                  id: 'optimised',
                  title: 'Suggestion Engine',
                  blurb: 'A few choices from you — what you will pay, what happens to the home, how much could be locked away — then it models every way to set it up and shows the strongest for each goal.',
                  chip: 'Most people start here',
                },
              ]}
            />
          </Card>
          <GfAnalysisSetupModal
            onOpenFamilyGuide={() => { setGfSetupOpen(false); setShowFamilyGuide(true); }}
            isOpen={gfSetupOpen}
            onClose={() => { setGfSetupOpen(false); setGfSetupSeen(true); }}
            factFind={factFind}
            initial={gfSetup}
            reasonableValue={reasonableValueToday}
            homeValue={homeValue}
            askNewMoney={!hasAnsweredNewMoneyProfile(factFind)}
            tbcPerson={tbcPersonNeedingAnswer}
            onContinue={(next, extras) => {
              setGfSetup(next);
              setGfSetupSeen(true);
              setGfSetupOpen(false);
              if (extras.newMoneyProfile) answerNewMoneyProfile(extras.newMoneyProfile); else setNewMoneySkipped(true);
              if (tbcPersonNeedingAnswer) { if (extras.tbcAmount != null) answerTbc(extras.tbcAmount); else setTbcSkipped(true); }
              if (setFactFind) {
                setFactFind((prev) => ({
                  ...prev,
                  goals: {
                    ...(prev.goals || {}),
                    gfHomeRoute: next.homeRoute ?? 'all',
                    gfPurchaseState: next.purchaseState || '',
                    gfAnnuityShareCapPct: Number(next.annuityShareCapPct),
                    gfEmergencyReserve: Number(next.emergencyReserve) || 0,
                    gfOldUnitPaysAtMove: next.oldUnitPaysAtMove !== false,
                    annuityAppetite: next.annuityAppetite ?? 'unsure',
                    annuityResidual: next.annuityResidual ?? 'both',
                  },
                }));
              } else {
                setAnnuityPrefSkipped(true);
              }
            }}
          />

          {strategyMode === 'manual' && (<>
          <Card>
            <h3 className="text-xl font-bold text-gray-800 mb-4">Your arrangement</h3>
            <div className="space-y-4">
              <label className="flex items-start gap-3 cursor-pointer p-3 rounded-lg border border-slate-200 hover:bg-slate-50">
                <input type="checkbox" checked={gfInputs.justMovingIn} onChange={(e) => setField('justMovingIn', e.target.checked)} className="mt-1 rounded" />
                <span>
                  <span className="block font-medium text-gray-800">No cash or asset transfer — I&apos;m just moving in</span>
                  <span className="block text-sm text-gray-500">Moving in with family without paying for the right to live there. No granny-flat interest is created, so nothing counts as a gift — but you&apos;re assessed as a non-homeowner.</span>
                </span>
              </label>

              {!justMovingIn && (<>
              {hasHome && (
                <label className="flex items-start gap-3 cursor-pointer p-3 rounded-lg border border-slate-200 hover:bg-slate-50">
                  <input type="checkbox" checked={gfInputs.transferHome} onChange={(e) => setField('transferHome', e.target.checked)} className="mt-1 rounded" />
                  <span>
                    <span className="block font-medium text-gray-800">Transfer my home to the family</span>
                    <span className="block text-sm text-gray-500">Your home ({formatCurrency(homeValue)}) is signed over in return for the right to live with them.</span>
                  </span>
                </label>
              )}

              <CurrencyInput
                label={gfInputs.transferHome ? 'Extra cash paid on top (if any)' : 'Cash you will pay the family'}
                value={gfInputs.amountPaid ?? ''}
                onChange={(v) => setField('amountPaid', v ?? 0)}
                className=""
              />

              {!gfInputs.transferHome && (
                <CurrencyInput
                  label={<span>Value of a home whose title you transfer <InfoIcon text="The title of a home you live in, signed over for the right (instead of, or as well as, cash) — taken at face value, so none of it is a gift. An investment property is different: signed over, it is treated as assets given and tested against the reasonable value, like cash. Leave at 0 if you're only paying cash." /></span>}
                  value={gfInputs.propertyTransferredValue ?? ''}
                  onChange={(v) => setField('propertyTransferredValue', v ?? 0)}
                  className=""
                />
              )}

              <CurrencyInput
                label={<span>Construction / extension cost (if building) <InfoIcon text="Money you pay to build or extend the home for your accommodation. It's funded from your assets just like the cash above, and counts towards your granny-flat interest — you receive the dwelling, so it isn't treated as a gift up to its value." /></span>}
                value={gfInputs.constructionCost ?? ''}
                onChange={(v) => setField('constructionCost', v ?? 0)}
                className=""
              />

              {/* THE REASONABLENESS TEST, WHERE THE AMOUNT IS ENTERED — the single question that
                  decides whether this is a clean transfer or a five-year penalty. */}
              <GrannyFlatArrangementNote
                entryContribution={entryContribution}
                interestValue={interestValue}
                reasonableValue={reasonableValue}
                deprivation={deprivation}
                keepingHome={keepingHome}
                formerHomeValue={homeValue}
              />

              {!gfInputs.transferHome && hasHome && cashOutlay > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">How will you fund the {formatCurrency(cashOutlay)}{constructionCost > 0 && amountPaid > 0 ? ' (cash + build)' : constructionCost > 0 ? ' build cost' : ''}?</label>
                  <div className="flex flex-col gap-2">
                    <label className="flex items-center gap-2"><input type="radio" name="gf-fund" checked={gfInputs.fundFrom === 'savings'} onChange={() => setField('fundFrom', 'savings')} /> <span>From my savings</span></label>
                    <label className="flex items-center gap-2"><input type="radio" name="gf-fund" checked={gfInputs.fundFrom === 'sell_home'} onChange={() => setField('fundFrom', 'sell_home')} /> <span>By selling my home (proceeds fund the payment; the rest goes to the bank)</span></label>
                  </div>
                  {sellingHomeToFund && saleResidual > 0 && (
                    <div className="mt-3">
                      <ProceedsDeploymentFields factFind={factFind} setFactFind={setFactFind} available={saleResidual} value={deployment} onChange={(d: ProceedsDeploymentValue) => setField('deployment', d)} idPrefix="gf" />
                    </div>
                  )}
                </div>
              )}

              {cashFromSavings > 0 && (
                <div>
                  {fundingPending && (
                    <p className="text-xs text-slate-700 bg-white border border-slate-200 border-l-4 border-l-amber-400 rounded-r-lg p-2 mb-2">Choose how to fund the {formatCurrency(cashFromSavings)} below — the Financial Impact updates once you&apos;ve picked where the money comes from.</p>
                  )}
                  <AssetFundingAllocator
                    factFind={factFind}
                    target={cashFromSavings}
                    funding={funding}
                    onChange={(m: Record<string, number>) => setField('funding', m)}
                    cgtOverrides={fundingCgtOverrides}
                    onCgtOverridesChange={(o: Record<string, number | null>) => setField('fundingCgtOverrides', o)}
                    targetLabel={constructionCost > 0 ? 'Cash + construction to fund' : 'Cash to pay the family'}
                    help="Choose how much to draw from each asset. The account-based pension is drawn before shares by default (drawing it is tax-free; selling shares can trigger CGT) — adjust as you like."
                  />
                </div>
              )}
              </>)}

              {keepingHome && shouldSimulate && (
                <div className="rounded-lg border border-slate-200 border-l-4 border-l-blue-500 bg-slate-50 p-3">
                  <p className="text-sm font-medium text-gray-800 flex items-center gap-1 mb-1"><Home size={16} /> The home you&apos;re keeping ({formatCurrency(homeValue)})</p>
                  <p className="text-xs text-gray-600 mb-3">Once the granny flat becomes your principal home, your former home is <strong>no longer exempt</strong> — Centrelink counts it as an assessable asset straight away (there&apos;s no 12-month or 2-year grace period for this kind of move).</p>
                  <div className="flex flex-col gap-2">
                    <label className="flex items-center gap-2"><input type="radio" name="gf-kept-home" checked={gfInputs.keptHomeUse !== 'rented'} onChange={() => setField('keptHomeUse', 'vacant')} /> <span className="text-sm">Leave it vacant <span className="text-gray-500">(counted as an asset; no income)</span></span></label>
                    <label className="flex items-center gap-2"><input type="radio" name="gf-kept-home" checked={gfInputs.keptHomeUse === 'rented'} onChange={() => setField('keptHomeUse', 'rented')} /> <span className="text-sm">Rent it out <span className="text-gray-500">(asset + the net rent counts as income)</span></span></label>
                  </div>
                  {gfInputs.keptHomeUse === 'rented' && (
                    <>
                      <div className="grid grid-cols-2 gap-3 mt-3">
                        <CurrencyInput label="Weekly rent ($)" value={gfInputs.keptHomeRent ?? ''} onChange={(v) => setField('keptHomeRent', v ?? 0)} className="" />
                        <CurrencyInput label="Annual expenses ($)" value={gfInputs.keptHomeExpenses ?? ''} onChange={(v) => setField('keptHomeExpenses', v ?? 0)} className="" />
                      </div>
                      {((factFind?.home?.likelyRent || 0) > 0 || (factFind?.home?.likelyExpenses || 0) > 0) && (
                        <p className="text-xs text-gray-500 mt-2">Pre-filled from your client profile — adjust if needed.</p>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          </Card>

          {hasArrangement && (
            <Card>
              <h3 className="text-xl font-bold text-gray-800 mb-4">How Centrelink sees it</h3>
              <div className="space-y-2">
                <div className="rounded-lg border border-slate-200 border-l-4 border-l-blue-500 bg-slate-50 p-3 flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-800">Homeowner status</span>
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${isHomeowner ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}`}>{isHomeowner ? 'Homeowner (right exempt)' : 'Non-homeowner'}</span>
                </div>
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm space-y-1">
                  <div className="flex justify-between"><span className="text-gray-600">Total you give</span><span className="font-semibold">{formatCurrency(entryContribution)}</span></div>
                  <div className="flex justify-between"><span className="text-gray-600">Assessed value of your right <InfoIcon text={assessedValueTooltip} /></span><span className="font-semibold">{formatCurrency(interestValue)}</span></div>
                  <div className="flex justify-between"><span className="text-gray-600">Counted as a gift (5 yrs)</span><span className={`font-semibold ${deprivation > 0 ? 'text-amber-700' : 'text-green-700'}`}>{formatCurrency(deprivation)}</span></div>
                </div>
                {deprivation > 0 && (
                  <p className="text-xs text-slate-700 bg-white border border-slate-200 border-l-4 border-l-amber-400 rounded-r-lg p-2">Paying close to the assessed value ({formatCurrency(interestValue)}) would avoid this gift. The gifted amount is counted for 5 years, then drops off.</p>
                )}
              </div>
            </Card>
          )}
          </>)}

          {/* With the funding inputs, not after the suggestion cards: money spent clearing a
              debt is money the rest of the plan cannot use. Renders nothing when the client has no debt. */}
          <DebtPayoffStrategy
            factFind={factFind as never}
            debtPayments={debtPayments}
            setDebtPayments={setDebtPayments}
            debtFunding={debtFunding}
            setDebtFunding={setDebtFunding}
            repaymentBasis={debtRepaymentBasis}
            setRepaymentBasis={setDebtRepaymentBasis}
            competingUseNote="The money comes from the same assets that can fund the granny flat arrangement, so what you spend here reduces what is left to pay or build with."
          />

          {strategyMode === 'optimised' && (
            <Card>
              <div className="flex items-center gap-3 mb-1">
                <IconTile icon={TrendingUp} tone="brand" variant="soft" size="sm" className="hidden sm:inline-flex" />
                <h3 className="text-xl font-bold text-gray-800">Suggested strategies</h3>
                <button
                  type="button"
                  onClick={() => setGfSetupOpen(true)}
                  className="ml-auto text-sm font-medium text-blue-700 hover:text-blue-900 hover:underline whitespace-nowrap"
                >
                  Adjust setup
                </button>
              </div>
              {needsNewMoneyAnswer ? (
                <NewMoneyProfileGate
                  onAnswer={answerNewMoneyProfile}
                  onSkip={() => setNewMoneySkipped(true)}
                  contextLine="Some of the structures I'll compare invest money that gets freed up — for example after selling your home to fund the arrangement."
                />
              ) : needsTbcAnswer ? (
                <TransferCapGate
                  name={(tbcPersonNeedingAnswer === 'partner' ? factFind?.partner?.firstName : factFind?.client?.firstName) || 'this person'}
                  onAnswer={answerTbc}
                  onSkip={() => setTbcSkipped(true)}
                />
              ) : needsAnnuityAnswer ? (
                <AnnuityPreferenceGate
                    offerGuaranteed={guaranteedShapeAvailable(factFind as never)}
                    guaranteeWindow={guaranteeWindowWords({
                        age: factFind?.client?.dob ? calculateAge(factFind.client.dob) : null,
                        sex: factFind?.client?.sex,
                        partnerAge: factFind?.adviceFor === 'couple' && factFind?.partner?.dob ? calculateAge(factFind.partner.dob) : null,
                        partnerSex: factFind?.partner?.sex,
                        ownerName: factFind?.client?.firstName,
                        partnerName: factFind?.partner?.firstName,
                    })}
                  onAnswer={answerAnnuityPreference}
                  onSkip={() => setAnnuityPrefSkipped(true)}
                  contextLine="One of the structures I'll compare uses the money left after paying for your right to live there."
                />
              ) : seLoading ? (
                <SuggestionEngineAnalysisLoader
                  title="Optimising your granny-flat strategy"
                  subtitle="We're modelling every way to set up the arrangement — this only takes a moment."
                />
              ) : gfStrategies.length === 0 ? (
                <p className="text-sm text-gray-600 mt-3">We couldn&apos;t generate suggestions for this scenario. Use <strong>Manual Control</strong> to model your options.</p>
              ) : (
                <>
                  <p className="text-sm text-gray-600 mt-1 mb-2">
                    I&apos;ve modelled <strong>{gfStrategies.filter((s) => !isGfBaseline(s)).length}</strong> ways to set up the arrangement — paying Centrelink&apos;s reasonable value of <strong>{formatCurrency(Math.round(Number(gfSuggestions?.reasonableValue) || 0))}</strong> so nothing counts as a gift{Number(gfSetup.knownAmount) > 0 ? `, your own ${formatCurrency(Number(gfSetup.knownAmount))}` : ''}, and {gfSetup.homeRoute === 'keep' ? 'keeping the home' : gfSetup.homeRoute === 'sell' ? 'selling the home' : gfSetup.homeRoute === 'transfer' ? 'signing the home over' : 'every route for the home'} — and picked the strongest for each goal below. Each card carries its own figures, measured against <strong>not entering the arrangement</strong> — the position on the right. <strong>Explain this</strong> sets a plan out in plain words, with the year-by-year detail behind it; <strong>What could go wrong</strong> is the honest list of its risks.
                  </p>
                  <div className="mb-4">
                    <button type="button" onClick={() => setShowAllSeStrategies(true)} className="text-sm font-semibold text-blue-600 hover:text-blue-800 hover:underline">
                      Compare all {gfStrategies.length} strategies →
                    </button>
                  </div>
                  <GoalChipRow value={optimisationFocus} onChange={setOptimisationFocus} baselineWord="not entering the arrangement" />
                  {optimisationFocus === 'covered' && coveredSameAsEstate && (
                    <div className="mb-4"><SameAsBestEstateNote onShowBestEstate={() => setOptimisationFocus('legacy')} /></div>
                  )}
                  {visibleSeCards.length === 0 && !(optimisationFocus === 'covered' && coveredSameAsEstate) && (
                    <p className="mb-4 rounded-lg border border-slate-200/70 border-l-4 border-l-amber-500 bg-white px-3 py-2 text-sm text-slate-700">
                      {emptyGoalLine(optimisationFocus, gfStrategies.filter((s) => !isGfBaseline(s)), 'not entering the arrangement')}
                    </p>
                  )}
                  <div className="space-y-4">
                    {visibleSeCards.map(({ strategy: leader, badges, belowBaseline }) => {
                      const m = gfMoveOf(leader, hasHome, homeValue);
                      const annuity = gfAnnuityOf(leader, factFind);
                      const nilResidual = annuity && Math.round(annuity.residualPercent) === 0 ? annuity : null;
                      const horizonWarning = gfAnnuityHorizonWarningOf(leader, factFind);
                      const base = gfStayPut?.snapshot || {};
                      const snap = leader.snapshot || {};
                      const terms = deploymentAnnuityTerms(leader.inputs?.deployment as ProceedsDeploymentValue | undefined, isCouple);
                      const moneyRows = [
                        ...(leader.fundingBreakdown || []),
                        ...describeResidualDeployment(leader.inputs?.deployment as ResidualDeployment | undefined),
                        ...((leader.fundingCgt ?? 0) > 0 ? [{ label: 'Capital gains tax on the shares sold', amount: leader.fundingCgt ?? 0 }] : []),
                      ];
                      return (
                        <EngineStrategyCard
                          key={leader.id || leader.name}
                          name={leader.name || ''}
                          description={leader.description}
                          badges={[...badges, m.deprivation > 0 ? `${formatCurrency(m.deprivation)} gift` : (m.entryContribution > 0 ? 'No gift' : 'Nothing paid')]}
                          belowBaseline={belowBaseline}
                          snapshot={snap}
                          baseline={gfStayPut?.snapshot ?? null}
                          baselineWord="not entering"
                          legacyYears={localLegacyYears}
                          flow={flowFor(leader)}
                          goalNote={coveredNoteFor(leader)}
                          inBrief={(
                            <GfInBrief
                              entryContribution={m.entryContribution}
                              propertyTransferred={m.propertyTransferred}
                              justMovingIn={m.justMovingIn}
                              deprivation={m.deprivation}
                              home={m.home}
                              keptHomeRentPerWeek={m.keptHomeRent}
                              saleResidual={m.saleResidual}
                              pensionBefore={Number(base.pension) || 0}
                              pensionAfter={Number(snap.pension) || 0}
                              cashBefore={Number(base.netCashflow) || 0}
                              cashAfter={Number(snap.netCashflow) || 0}
                              planLegacy={snap.legacyValue ?? null}
                              baseLegacy={base.legacyValue ?? null}
                              legacyYears={localLegacyYears}
                              annuity={annuity}
                            />
                          )}
                          worthKnowing={(nilResidual || horizonWarning || (leader.fundingShortfall ?? 0) > 0) ? (
                            <>
                              {(leader.fundingShortfall ?? 0) > 0 && <>About {formatCurrency(leader.fundingShortfall)} of the payment cannot be funded from available assets — this is not yet an arrangement that can go ahead.{' '}</>}
                              {nilResidual && <>The estate ends behind not entering mainly because {nilResidualAnnuityText(nilResidual)}. The income is guaranteed for life; the capital is not coming back.{' '}</>}
                              {horizonWarning}
                            </>
                          ) : null}
                          alternative={alternativeAssetNote(leader, gfStrategies, (s) => s.snapshot?.legacyValue ?? null, Number(localLegacyYears) || 5)}
                          moneyRows={moneyRows.length > 0 ? { heading: "How it's funded", rows: moneyRows } : null}
                          isSelected={leader.id === selectedSeId}
                          onSelect={() => selectEngineStrategy(leader)}
                          onExplain={() => { setExplainSeId(leader.id ?? null); setShowGfExplanation(true); }}
                          onRisks={() => setRiskStrategy(leader)}
                          onGenerateSoa={() => generateGfSoa(leader)}
                        >
                          {/* The annuity's terms, where selling the home funds one (owner, 2026-09-01). */}
                          {terms && (
                            <EngineAnnuityTerms
                              className="mt-3"
                              terms={terms}
                              factFind={factFind}
                              quote={<AnnuityQuoteControl
                                draft={gfAnnuityQuoteDraft}
                                onDraft={setGfAnnuityQuoteDraft}
                                committed={gfAnnuityQuote}
                                onCommit={setGfAnnuityQuote}
                              />}
                            />
                          )}
                        </EngineStrategyCard>
                      );
                    })}
                  </div>
                  {gfSetAside.length > 0 && (
                    <div className="mt-4 rounded-lg border border-slate-200 border-l-4 border-l-amber-400 bg-white py-2 pl-3 pr-2 text-[13px] leading-relaxed text-slate-700">
                      <p><span className="font-semibold">{gfSetAside.length === 1 ? 'One strategy was set aside.' : `${gfSetAside.length} strategies were set aside.`}</span> A plan is not promoted as the best result when the client&apos;s age and health point to a shorter time frame than it needs.</p>
                      <ul className="mt-1 space-y-1">
                        {gfSetAside.map((sa) => (
                          <li key={sa.strategy.id || sa.strategy.name}>
                            <span className="font-semibold">{sa.strategy.name}</span> would have led on {sa.labels.map((l) => labelMap[l] || l).join(' and ')}. {sa.warning}{' '}
                            <button type="button" onClick={() => setSeDeepDive({ projections: sa.strategy.projections || [], name: sa.strategy.name || '', id: sa.strategy.id })} className="font-semibold text-blue-700 underline decoration-dotted underline-offset-2 hover:text-blue-900">See it anyway</button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  <p className="text-xs text-slate-500 mt-4">These are model illustrations based on what you entered. To model your own variation, switch to <strong>Manual Control</strong> above. Always get legal and financial advice before committing.</p>
                </>
              )}
            </Card>
          )}
        </div>

          {/* THE SAME 3/2 SPLIT AS AGED CARE (2026-08-27). `xl:` is deliberate: lg splits far too
              early for a panel this dense, and a 1920 screen at 100% zoom is only a ~1330px viewport. */}
        <div className="xl:col-span-2 space-y-6 xl:sticky xl:top-8 self-start">
          <PlanSummaryPanel
            strategyName={gfPlanSummaryName}
            baselineLabel="not entering the arrangement"
            inBrief={gfInBrief}
            onReadFullExplanation={gfPlanSummaryName ? () => { setExplainSeId(null); setShowGfExplanation(true); } : null}
            onViewProjections={() => (railHoldsStayPut ? setShowStayPutProjections(true) : setSeDeepDive({ projections: panelModel.projections, name: gfPlanSummaryName ?? undefined }))}
            noStrategyHeading={strategyMode === 'optimised' ? 'Not entering the arrangement — for comparison' : (fundingPending && shouldSimulate ? 'Not funded yet — your position today' : 'No plan yet — your position today')}
            noStrategyNote={strategyMode === 'optimised' ? (
              <><span className="font-semibold text-slate-700">For comparison</span> while you choose. Each strategy card carries its own figures —{' '}
              <span className="font-semibold text-slate-700">Select</span> one, then{' '}
              <span className="font-semibold text-slate-700">Explain this</span> and{' '}
              <span className="font-semibold text-slate-700">What could go wrong</span> on the card.</>
            ) : fundingPending && shouldSimulate ? (
              <>
                The figures here are still{' '}
                <span className="font-semibold text-slate-700">life as it is</span>, because nothing
                moves until you choose{' '}
                <span className="font-semibold text-slate-700">where the {formatCurrency(cashOutlay)} comes from</span> on
                the left.
              </>
            ) : (
              <>
                Nothing has been given or transferred yet. This is your{' '}
                <span className="font-semibold text-slate-700">current position</span> — enter what
                you would give on the left and every figure here is restated against{' '}
                <span className="font-semibold text-slate-700">not entering the arrangement</span>.
              </>
            )}
            costOverviewLabel="What the arrangement costs"
            costOverview={
              <div className="bg-gray-800 text-white rounded-xl p-4">
                <div className={`flex items-start justify-between gap-3 ${(railHoldsStayPut || panelModel.entryContribution > 0) ? 'mb-3 pb-3 border-b border-white/15' : ''}`}>
                  <p className="text-gray-400 text-xs leading-snug">{railHoldsStayPut ? <>Centrelink&apos;s reasonable value<br />of the right to live there</> : <>What you give<br />for the right to live there</>}</p>
                  <div className="text-right">
                    {railHoldsStayPut ? (
                      <>
                        <p className="text-3xl font-bold text-white leading-none">{reasonableValueToday != null ? formatCurrency(Math.round(reasonableValueToday)) : '…'}</p>
                        <p className="text-gray-300 font-semibold text-xs mt-1">at the start date, at your age</p>
                      </>
                    ) : panelModel.entryContribution > 0 ? (
                      <>
                        <p className="text-3xl font-bold text-white leading-none">{formatCurrency(panelModel.entryContribution)}</p>
                        <p className="text-gray-300 font-semibold text-xs mt-1">
                          {panelModel.propertyTransferred > 0 ? 'cash and property' : 'paid up front'}
                        </p>
                      </>
                    ) : (
                      <>
                        <p className="text-lg font-bold text-white leading-tight">Nothing entered yet</p>
                        <p className="text-gray-400 text-xs mt-1">Enter it on the left</p>
                      </>
                    )}
                  </div>
                </div>
                {railHoldsStayPut ? (
                  <p className="text-xs text-gray-400">
                    {strategyMode === 'optimised'
                      ? 'None of this is in the figures below, which are life as it is. Each card says what it pays, where the money comes from and what happens to the home.'
                      : 'Pay cash up to this and nothing counts as a gift; a home signed over or a build paid for is taken at face value. None of it is in the figures below until a plan is built.'}
                  </p>
                ) : panelModel.entryContribution > 0 && (
                <div className="space-y-3 text-sm">
                  {panelModel.amountPaid > 0 && (
                    <div className="flex justify-between"><span className="text-gray-300">Cash paid</span><span className="font-semibold">{formatCurrency(panelModel.amountPaid)}</span></div>
                  )}
                  {panelModel.propertyTransferred > 0 && (
                    <div className="flex justify-between"><span className="text-gray-300">Property transferred</span><span className="font-semibold">{formatCurrency(panelModel.propertyTransferred)}</span></div>
                  )}
                  <div className="flex justify-between pt-3 border-t border-white/15">
                    <span className="text-gray-300">Centrelink&apos;s reasonable value</span>
                    <span className="font-semibold">{formatCurrency(panelModel.reasonableValue)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-300">Counted as a gift</span>
                    <span className={`font-semibold ${panelModel.deprivation > 0 ? 'text-amber-300' : 'text-green-400'}`}>
                      {panelModel.deprivation > 0 ? formatCurrency(panelModel.deprivation) : 'Nothing'}
                    </span>
                  </div>
                  {panelModel.fundingCgt > 0 && (
                    <div className="flex justify-between"><span className="text-gray-300">Capital gains tax on funding</span><span className="font-semibold text-rose-300">{formatCurrency(panelModel.fundingCgt)}</span></div>
                  )}
                </div>
                )}
                {!railHoldsStayPut && panelModel.deprivation > 0 && (
                  <p className="text-xs text-amber-200 bg-amber-900/30 rounded-lg px-3 py-2 mt-3">
                    Paying more than the reasonable value makes the excess a <strong>gift</strong>. Centrelink
                    still counts it as your asset — and deems it to earn income — for five years, even though
                    the money has gone.
                  </p>
                )}
                {!railHoldsStayPut && panelModel.fundingShortfall > 0 && (
                  <p className="text-xs text-amber-200 bg-amber-900/30 rounded-lg px-3 py-2 mt-3">
                    <strong>Still to fund: {formatCurrency(panelModel.fundingShortfall)}.</strong> Choose where
                    the rest comes from on the left — until then this is not yet an arrangement that can go ahead.
                  </p>
                )}
              </div>
            }
            liquidity={gfRunway && (
              <>
                <p className="text-lg font-bold leading-tight tracking-tight text-slate-900">{gfRunway.headline}</p>
                <div className="mt-2">
                  <FundingRunway
                    coveredPct={gfRunway.coveredPct}
                    leftLabel={gfRunway.leftLabel}
                    rightLabel={gfRunway.rightLabel}
                    depleted={gfRunway.depleted}
                    flag={gfRunway.flag}
                  />
                </div>
                <p className="mt-1.5 text-[11px] text-slate-400">{gfRunway.basis}</p>
              </>
            )}
            financialImpact={
              <FinancialImpactPanel
                embedded
                /* Not entering IS the panel until a plan is named: one figure per row, no change. */
                baselineOnly={railHoldsStayPut}
                before={railBefore}
                after={(railHoldsStayPut ? railBefore : (panelModel.after as FinancialPosition)) ?? railBefore}
                legacyYears={localLegacyYears}
                setLegacyYears={setLocalLegacyYears}
                onViewProjections={() => (railHoldsStayPut ? setShowStayPutProjections(true) : setSeDeepDive({ projections: panelModel.projections, name: gfPlanSummaryName ?? undefined }))}
                projections={railProjections}
                cashFlowAfterValue={railHoldsStayPut ? (railBefore?.cashFlow ?? null) : (panelModel.projections[0]?.netCashflow ?? panelModel.projections[0]?.cashFlow ?? panelModel.after?.cashFlow ?? null)}
                netWorthAfterValue={railHoldsStayPut ? (railBefore?.netWorth ?? null) : (panelModel.after?.netWorth ?? null)}
                legacyEstimateOverride={railHoldsStayPut ? manualBaselineLegacy : (panelModel.legacyEstimate ?? null)}
                /* Measured against NOT entering the arrangement, projected over the same years —
                   today's net worth is a different question and flatters every strategy. */
                legacyBaselineValue={manualBaselineLegacy}
                baselineLabel={manualBaselineLegacy != null ? 'not entering the arrangement' : undefined}
              />
            }
          />
        </div>
      </div>
      )}

      {step === 3 && (hasArrangement || selectedSeId) && (
        <Card className="mt-6">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:justify-between">
            <div className="flex items-start gap-3">
              <IconTile icon={Users} tone="brand" variant="soft" size="sm" className="hidden sm:inline-flex" />
              <div>
                <h3 className="text-xl font-bold text-gray-800">Family discussion &amp; documentation guide</h3>
                <p className="text-sm text-gray-600 max-w-2xl">Settle the questions a solicitor will ask — what the right covers, who pays what, fairness to other family, the &quot;what-ifs&quot; — and print a brief to take with you. Your figures from here are carried across.</p>
              </div>
            </div>
            <Button variant="brand" size="sm" iconRight={ArrowRight} className="shrink-0" onClick={() => setShowFamilyGuide(true)}>Open the guide</Button>
          </div>
        </Card>
      )}

      {step === 3 && (
        <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
          <button type="button" onClick={() => goToStep(2)} className="inline-flex h-11 items-center gap-2 rounded-lg border border-slate-300 bg-white px-5 text-[14px] font-semibold text-slate-700 hover:border-blue-300 hover:text-blue-900">
            <ChevronLeft size={16} /> Back to where {clientName} {factFind?.client?.firstName ? 'stands' : 'stand'} today
          </button>
          <button type="button" onClick={onBack} className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-blue-700 hover:text-blue-900">
            <BookOpen size={15} /> Read the overview of granny flat arrangements
          </button>
        </div>
      )}

      {showAllSeStrategies && gfSuggestions && (
        <AllStrategiesModal<GfStrategyView>
          strategies={gfStrategies}
          legacyYears={localLegacyYears}
          selectedId={selectedSeId}
          isBaseline={isGfBaseline}
          onSelect={selectEngineStrategy}
          onDeepDive={(s) => setSeDeepDive({ projections: s.projections || [], name: s.name || '', id: s.id })}
          onClose={() => setShowAllSeStrategies(false)}
        />
      )}

      {/* "Understanding your plan" — the recap on its own surface; the year-by-year lives in its footer. */}
      {showGfExplanation && (
        <PlanExplanationModal
          strategyName={explainStrategy?.name ?? gfPlanSummaryName}
          onClose={() => { setShowGfExplanation(false); setExplainSeId(null); }}
          footer={(
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                setShowGfExplanation(false);
                if (explainStrategy) setSeDeepDive({ projections: explainStrategy.projections || [], name: explainStrategy.name || '', id: explainStrategy.id });
                else setSeDeepDive({ projections: panelModel.projections, name: gfPlanSummaryName ?? undefined });
                setExplainSeId(null);
              }}
            >
              See the year-by-year figures
            </Button>
          )}
        >
          {gfRecapNode}
        </PlanExplanationModal>
      )}
      {riskStrategy && (
        <StrategyRisksPanel
          strategyName={riskStrategy.name || ''}
          onClose={() => setRiskStrategy(null)}
          risks={buildStrategyRisks(riskKindsFromCandidateId(String(riskStrategy.id || '')), {
            familyToKeepWhole: gfFamilyToKeepWhole(factFind, gfMoveOf(riskStrategy, hasHome, homeValue)),
            horizonWarning: gfAnnuityHorizonWarningOf(riskStrategy, factFind),
            stampDuty: Number((riskStrategy.inputs?.deployment as { ipStampDuty?: number } | undefined)?.ipStampDuty) || null,
          })}
        />
      )}

      {showStayPutProjections && (
        <ProjectionsModal
          onClose={() => setShowStayPutProjections(false)}
          projections={stayPutProjections as never}
          factFind={factFind as never}
          initialFinancials={(todayFinancials ?? before) as never}
          costLabel={null}
        />
      )}

      {seDeepDive && (
        <ProjectionsModal
          onClose={() => setSeDeepDive(null)}
          projections={(modalPeriodMode === 'calendar_year' && calendarYearProjections ? calendarYearProjections : seDeepDive.projections) as never}
          {...(deepDiveCandidate ? { onPeriodModeChange: setModalPeriodMode, isReloading: modalPeriodMode === 'calendar_year' && !calendarYearProjections } : {})}
          factFind={factFind as never}
          initialFinancials={(todayFinancials ?? before) as never}
          costLabel={null}
        />
      )}
    </div>
  );
};

/**
 * Family discussion & documentation guide — its own page (feedback #7; reworked 2 Oct 2026).
 *
 * WHAT IT IS NOW. A set of questions the family answers together — what the right covers, who pays
 * what, fairness to the other family, the "what-ifs" — rather than a page to read, with a brief
 * at the end they can print and hand to a solicitor (gfSolicitorBrief.ts). "Still to decide" is a
 * real answer everywhere: the brief lists those as questions for the solicitor instead of
 * pretending they are settled.
 *
 * WHERE THE ANSWERS LIVE. On the client file (`grannyFlat.familyGuide`, strong-typed in
 * factFindSchema.ts), so the family can come back to it and the brief can be printed again. Text
 * is committed on blur, not per keystroke — a fact-find change re-runs the analyser's position.
 */
interface GrannyFlatFamilyGuideProps {
  factFind: DraftFactFind;
  setFactFind?: React.Dispatch<React.SetStateAction<DraftFactFind>>;
  /** The modelled arrangement's figures. Null when opened from the explainer, before anything is modelled. */
  figures?: GfSolicitorBriefFigures | null;
  assessedValueTooltip?: string;
  onBack: () => void;
  /** What the back button says — "Back to the calculator" by default. */
  backLabel?: string;
  /** The forward button at the foot of the page, where there is somewhere to go next (the explainer). */
  onContinue?: () => void;
  continueLabel?: string;
  onBackToDashboard?: () => void;
}

/** One question's answers as pills — the setup modal's Choice, in the guide's quieter voice. */
function GuidePick<T extends string>({ options, value, onChange, ariaLabel }: { options: Array<{ id: T; label: string }>; value: T | undefined; onChange: (v: T) => void; ariaLabel: string }) {
  return (
    <div className="flex flex-wrap gap-2" role="radiogroup" aria-label={ariaLabel}>
      {options.map((o) => (
        <button
          key={o.id}
          type="button"
          role="radio"
          aria-checked={value === o.id}
          onClick={() => onChange(o.id)}
          className={`rounded-full border px-3 py-1.5 text-[13px] font-semibold transition-colors ${value === o.id ? 'border-blue-600 bg-blue-50 text-blue-900' : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300'}`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

const asOptions = <T extends string>(labels: Record<T, string>, short?: Partial<Record<T, string>>): Array<{ id: T; label: string }> =>
  (Object.keys(labels) as T[]).map((id) => ({ id, label: short?.[id] ?? labels[id] }));

const GrannyFlatFamilyGuide = ({
  factFind, setFactFind, figures = null, assessedValueTooltip = '', onBack, backLabel = 'Back to the calculator', onContinue, continueLabel = 'Continue', onBackToDashboard,
}: GrannyFlatFamilyGuideProps) => {
  const saved = (factFind?.grannyFlat?.familyGuide || {}) as GrannyFlatFamilyGuideAnswers;
  const [answers, setAnswers] = useState<GrannyFlatFamilyGuideAnswers>(saved);
  const clientName = factFind?.client?.firstName || 'you';
  const partnerName = factFind?.adviceFor === 'couple' ? (factFind?.partner?.firstName || null) : null;
  const childrenCount = parseInt(String(factFind?.family?.childrenCount ?? ''), 10) || 0;

  /** Saves one answer locally and to the file; text fields call this on blur. */
  const commit = <K extends keyof GrannyFlatFamilyGuideAnswers>(key: K, value: GrannyFlatFamilyGuideAnswers[K]) => {
    const next = { ...answers, [key]: value };
    setAnswers(next);
    if (setFactFind) setFactFind((prev) => ({ ...prev, grannyFlat: { ...(prev.grannyFlat || {}), familyGuide: next } }));
  };
  const open = openQuestionsFor(answers);
  /** Opens and downloads the brief, and keeps a copy with the file: the answers plus the figures it quoted (owner, 3 Oct 2026). */
  const printBrief = () => {
    const generatedAt = new Date().toISOString();
    openGfSolicitorBrief({ clientName, partnerName, answers, figures, generatedAt });
    const brief = figures
      ? { generatedAt, entryContribution: figures.entryContribution, interestValue: figures.interestValue, deprivation: figures.deprivation, isHomeowner: figures.isHomeowner, dateEntered: figures.dateEntered }
      : { generatedAt };
    const next = { ...answers, brief };
    setAnswers(next);
    if (setFactFind) setFactFind((prev) => ({ ...prev, grannyFlat: { ...(prev.grannyFlat || {}), familyGuide: next } }));
  };

  const whatIfOptions = (key: keyof typeof GF_WHAT_IF_LABELS) => {
    const def = GF_WHAT_IF_LABELS[key] as Record<string, string>;
    return (Object.keys(def).filter((k) => k !== 'question') as Array<NonNullable<GrannyFlatFamilyGuideAnswers[typeof key]>>).map((id) => ({ id, label: def[id] }));
  };

  const panel = 'rounded-lg border border-slate-200 border-l-4 bg-white p-4';
  const q = 'text-sm font-semibold text-gray-900 mb-2';
  const input = 'w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-blue-500 focus:ring-blue-500';

  return (
    <div className="max-w-5xl mx-auto">
      <div className="flex items-center gap-2 mb-4 -ml-2">
        <Button variant="ghost" size="sm" icon={ChevronLeft} onClick={onBack}>{backLabel}</Button>
        {onBackToDashboard && <button onClick={onBackToDashboard} className="text-blue-600 hover:text-blue-800 font-medium text-sm">Back to Dashboard</button>}
      </div>
      <div className="flex items-center gap-4 mb-2">
        <IconTile icon={Users} tone="brand" variant="solid" size="lg" className="hidden sm:inline-flex" />
        <h1 className="text-3xl md:text-4xl font-bold text-gray-900"><GradientText>Family discussion &amp; documentation guide</GradientText></h1>
      </div>
      <p className="text-base text-gray-600 max-w-3xl mb-6">Work through these questions together. Each answer goes into a brief you can print and hand to a solicitor; anything still undecided is listed there as a question for them. This is general information to help the conversation — not legal advice.</p>

      <Card>
        {figures == null ? (
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 mb-4">
            <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide mb-1">The arrangement in summary</p>
            <p className="text-sm text-slate-600">Once you have modelled an arrangement in the calculator, your figures appear here and in the brief: what is being given, Centrelink&apos;s value of the right, anything counted as a gift, and your homeowner status.</p>
          </div>
        ) : (
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 mb-4">
            <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide mb-2">The arrangement in summary</p>
            <ul className="text-sm text-slate-700 space-y-1">
              <li className="flex justify-between"><span>Total being given (cash, home or build)</span><span className="font-semibold">{formatCurrency(figures.entryContribution)}</span></li>
              <li className="flex justify-between"><span>Centrelink&apos;s value of your right to live there <InfoIcon text={assessedValueTooltip} /></span><span className="font-semibold">{formatCurrency(figures.interestValue)}</span></li>
              <li className="flex justify-between"><span>Amount counted as a gift (for 5 years)</span><span className={`font-semibold ${figures.deprivation > 0 ? 'text-amber-700' : 'text-green-700'}`}>{figures.deprivation > 0 ? formatCurrency(figures.deprivation) : 'None'}</span></li>
              <li className="flex justify-between"><span>Pension homeowner status</span><span className="font-semibold">{figures.isHomeowner ? 'Homeowner (right exempt)' : 'Non-homeowner'}</span></li>
              <li className="flex justify-between"><span>Start of the arrangement</span><span className="font-semibold">{figures.dateEntered || 'As soon as it is in place'}</span></li>
            </ul>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className={`${panel} border-l-blue-500 md:col-span-2`}>
            <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2"><Home size={16} className="text-blue-600" /> The arrangement</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="gf-guide-host" className={q}>Who would {partnerName ? `${clientName} and ${partnerName}` : clientName} be living with?</label>
                <input id="gf-guide-host" type="text" className={input} defaultValue={answers.hostNames || ''} placeholder="First names and relationship — e.g. Sam and Priya, our daughter and son-in-law" onBlur={(e) => commit('hostNames', e.target.value.trim())} />
              </div>
              <div>
                <p className={q}>What would be given for the right to live there?</p>
                <GuidePick
                  ariaLabel="What would be given"
                  value={answers.arrangementForm}
                  onChange={(v) => commit('arrangementForm', v)}
                  options={asOptions(GF_ARRANGEMENT_FORM_LABELS, { cash: 'Cash', build: 'Paying for a build or extension', transfer_home: 'Our home, signed over', buy_in_their_name: 'A home bought in their name', mixed: 'A mix, with cash on top', undecided: 'Still to decide' })}
                />
                <p className="text-xs text-slate-500 mt-2">The home, a build or a bought property, on its own, is taken at face value — no gift. Cash, or anything on top, is tested against Centrelink&apos;s reasonable value.</p>
              </div>
            </div>
          </div>

          <div className={`${panel} border-l-blue-500`}>
            <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2"><ShieldCheck size={16} className="text-blue-600" /> What the right covers</h4>
            <label htmlFor="gf-guide-includes" className={q}>Rooms, shared areas, parking, garden, visitors</label>
            <textarea id="gf-guide-includes" rows={3} className={input} defaultValue={answers.includes || ''} placeholder="e.g. The downstairs bedroom and bathroom, the kitchen and living room shared, one car space, grandchildren welcome any time" onBlur={(e) => commit('includes', e.target.value.trim())} />
            <p className={`${q} mt-3`}>Who pays the household bills and upkeep?</p>
            <GuidePick ariaLabel="Who pays the bills" value={answers.billsPaidBy} onChange={(v) => commit('billsPaidBy', v)} options={asOptions(GF_BILLS_LABELS, { me: 'I pay an agreed share', family: 'The family covers them', shared: 'Shared — still to write down', undecided: 'Still to decide' })} />
          </div>

          <div className={`${panel} border-l-violet-500`}>
            <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2"><Scale size={16} className="text-violet-600" /> Fairness to {childrenCount > 1 ? `your ${childrenCount} children` : 'other family'}</h4>
            <p className="text-sm text-slate-700 mb-2">One family member receives a large amount now, plus the property&apos;s future growth. How will the others be kept whole?</p>
            <GuidePick ariaLabel="Fairness to other family" value={answers.fairness} onChange={(v) => commit('fairness', v)} options={asOptions(GF_FAIRNESS_LABELS, { will: 'Adjust the Will', advance: 'Record it as an advance on inheritance', insurance: 'Life insurance tops up the others', care_exchange: 'A fair exchange for the care provided', not_needed: 'Not needed — agreed as fair', undecided: 'Still to decide' })} />
          </div>

          <div className={`${panel} border-l-teal-500`}>
            <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2"><PiggyBank size={16} className="text-teal-600" /> Care from the family</h4>
            <p className="text-sm text-slate-700 mb-2">Will a family member be providing substantial daily care? If so they may qualify for Carer Payment or Carer Allowance, which are separate from the granny flat rules — worth checking with Services Australia, and worth writing into the agreement.</p>
            <GuidePick ariaLabel="Care from the family" value={answers.careProvided} onChange={(v) => commit('careProvided', v)} options={asOptions(GF_CARE_LABELS, { yes: 'Yes — daily care', no: 'No — company and support', unsure: 'Not sure yet' })} />
          </div>

          <div className={`${panel} border-l-amber-500`}>
            <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2"><AlertTriangle size={16} className="text-amber-600" /> The &quot;what-ifs&quot; to settle up front</h4>
            <p className="text-sm text-slate-700 mb-3">The agreement should say what happens in each of these. Leaving within five years can also mean Centrelink looks at the gift again.</p>
            <div className="space-y-3">
              {(['ifHostSells', 'ifNeedCare', 'ifRelationshipEnds', 'ifHostDies'] as const).map((key) => (
                <div key={key}>
                  <p className={q}>{GF_WHAT_IF_LABELS[key].question}</p>
                  <GuidePick ariaLabel={GF_WHAT_IF_LABELS[key].question} value={answers[key]} onChange={(v) => commit(key, v as never)} options={whatIfOptions(key)} />
                </div>
              ))}
            </div>
          </div>

          <div className={`${panel} border-l-slate-400 md:col-span-2`}>
            <label htmlFor="gf-guide-notes" className={q}>Anything else the solicitor should know</label>
            <textarea id="gf-guide-notes" rows={3} className={input} defaultValue={answers.notes || ''} placeholder="Anything agreed between you that is not covered above" onBlur={(e) => commit('notes', e.target.value.trim())} />
          </div>
        </div>

        <div className="mt-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 rounded-lg border border-slate-200 bg-slate-50 p-4">
          <p className="text-sm text-slate-700">
            {open.length === 0
              ? <><span className="font-semibold text-slate-900">Every question has an answer.</span> The brief sets them out for the solicitor to turn into the agreement.</>
              : <><span className="font-semibold text-slate-900">{open.length} {open.length === 1 ? 'question is' : 'questions are'} still to decide.</span> That&apos;s fine — the brief lists {open.length === 1 ? 'it' : 'them'} as {open.length === 1 ? 'a question' : 'questions'} for the solicitor.</>}
          </p>
          <Button variant="brand" size="sm" icon={Printer} className="shrink-0" onClick={printBrief}>Print the solicitor brief</Button>
        </div>

        <p className="text-xs text-slate-500 mt-4">Take the brief to a <strong>solicitor</strong> to prepare the granny flat agreement and update your Will, and to <strong>Services Australia&apos;s Financial Information Service</strong> for the pension side.</p>
      </Card>

      <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
        <button type="button" onClick={onBack} className="inline-flex h-11 items-center gap-2 rounded-lg border border-slate-300 bg-white px-5 text-[14px] font-semibold text-slate-700 hover:border-blue-300 hover:text-blue-900">
          <ChevronLeft size={16} /> {backLabel}
        </button>
        {onContinue && (
          <button type="button" onClick={onContinue} className="inline-flex h-11 items-center gap-2 rounded-lg bg-gradient-to-r from-blue-600 to-teal-500 px-5 text-[14px] font-semibold text-white shadow-sm hover:from-blue-700 hover:to-teal-600">
            {continueLabel} <ArrowRight size={16} />
          </button>
        )}
      </div>
    </div>
  );
};

interface GrannyFlatPageProps {
  setActivePage: (page: string) => void;
  factFind: DraftFactFind;
  /** App-owned saved strategies — "Select Strategy" on an engine card saves here. */
  setSavedStrategies?: React.Dispatch<React.SetStateAction<Array<Record<string, unknown>>>>;
  /** Persists the new-money profile answer to the client record (App owns the fact-find state). */
  setFactFind?: React.Dispatch<React.SetStateAction<DraftFactFind>>;
  /**
   * Called with each generated Statement of Advice (the strategy plus its document snapshot) so it
   * lands on the server's advice register like aged care's (owner, 2 Oct 2026). App.tsx decides where.
   */
  onSoaGenerated?: (document: Record<string, unknown>) => void;
}

const GrannyFlatPage = ({ setActivePage, factFind, setSavedStrategies, setFactFind, onSoaGenerated }: GrannyFlatPageProps) => {
  const [view, setView] = useState<'explanation' | 'calculator'>('explanation');
  useEffect(() => {
    if (view === 'calculator') {
      scrollAppToTop();
    }
  }, [view]);
  if (view === 'explanation') return <GrannyFlatExplained setActivePage={setActivePage} onContinue={() => setView('calculator')} factFind={factFind} setFactFind={setFactFind} />;
  return <GrannyFlatAnalyser setActivePage={setActivePage} factFind={factFind} onBack={() => setView('explanation')} setSavedStrategies={setSavedStrategies} setFactFind={setFactFind} onSoaGenerated={onSoaGenerated} />;
};

export default GrannyFlatPage;
